// app/src/pages/CalendarPage.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "../api/Client";
import { SecureStorage } from "../storage/SecureStorage";
import { ModalHeader } from "../components/Header";
import { shareICS } from "../ICS/filesystem";
import { cancelAllBookingReminders, cancelBookingReminder, scheduleManyBookingReminders } from "../notifications/booking";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { APRENDE_PRODUCT_ID, createAprendeTask, deleteAprendeTask, extractBookingId, resolveBaseProductId } from "../utils/aprendeTasks";
import exerciseImage from "../assets/exercise.png";
import { API_BASE } from "../config";

/* =================== Tipos =================== */
type ActiveBooking = {
  booking_id: number;
  coach_name: string;
  product_name?: string;
  service_name?: string;
  payment_status?: string;
  start_date: string;   // ISO yyyy-mm-dd...
  start_time?: string;  // HH:mm:ss
  status: "active" | "cancelled" | string;
  service_id?: number;
  is_customer_late?: number;
  is_customer_absent?: number;
};

type UserBookingsApiItem = {
  id?: number;
  booking_id?: number;
  start_date?: string;
  date?: string;
  start_time?: string;
  hour?: string;
  service_id?: number;
  service?: string | null;
  service_name?: string | null;
  coach_name?: string | null;
  coach?: string | null;
  coach_profile_pic?: string | null;
  product_name?: string | null;
  product?: string | null;
  payment_status?: string | null;
  status?: string | null;
  is_customer_late?: number | string | null;
  is_customer_absent?: number | string | null;
  product_id?: number | null;
  active_product_id?: number | null;
  recover?: number | boolean | string | null;
  is_recover?: number | boolean | string | null;
  recovery?: number | boolean | string | null;
  coupon_id?: number | null;
  wild_card_id?: number | null;
};

// /api/mobile/user-bookings (id == booking_id)
type UserBooking = {
  id: number;                 // booking_id
  date: string;               // yyyy-mm-dd
  hour: string;               // HH:mm:ss
  service_id: number;
  service?: string | null;
  coach_name?: string | null;
  coach_profile_pic?: string | null; // URL
  product_id?: number | null;
  active_product_id?: number | null;
  was_recovery?: boolean;
  coupon_id?: number | null;
  wild_card_id?: number | null;
};

type ServiceItem = { service_id: number; service_name: string; centro?: string | null };
type PrimaryServiceItem = { primary_service_id: number; name: string };
type WeeklyHour = { service_id: number; date: string; hour: string; booked: number; capacity: number; duration?: number };
type HourlyCoach = { service_id: number; date: string; hour: string; coach_id: number; coach_name: string; booked: number; capacity: number };
type TimeslotOption = { id: number; name: string };

type WeeklyLimitEntry = {
  active_product_id: number | null;
  product_id: number;
  type_of_product: string; // "recurrent" o bono
  weekly_limit: number | null;
  used: number;
  remaining: number | null;
  total_limit?: number | null;
  target_date?: string | null;
  eligible_for_target_date?: boolean;
  expired_for_target_date?: boolean;
  recovery_eligible_for_target_date?: boolean;
};

type UserProduct = {
  id: number;
  name: string;
  image?: string | null;
  price?: number | null;
  serviceIds: number[];
  // Campo informativo; el crédito real lo calculamos con /user-weekly-limit
  sessionsRemaining: number | null;
  centro?: string | null;
  active_product_id?: number | null; // Added property
  subscription_status?: string | null;
  expiry_date?: string | null; // YYYY-MM-DDTHH:mm:ss
  type_of_product?: string | null;
  payment_status?: string | null;
  payment_method?: string | null;
  purchase_date?: string | null;
  active_product_status?: string | null;
  is_canceled_subscription?: number | null;
  target_date?: string | null;
  eligible_for_target_date?: boolean;
  expired_for_target_date?: boolean;
  recovery_eligible_for_target_date?: boolean;
};

type PastSessionDetail = {
  booking_id: number;
  product_name?: string;
  service_name?: string;
  coach_name?: string;
  date: string;      // YYYY-MM-DD
  time?: string;     // HH:mm
  comment?: string | null;
  payment_status?: string;
  is_customer_late: number;
  is_customer_absent: number;
};

type PreferredCoachItem = { coach_id: number; priority?: number | null };
type CouponStatus = "granted" | "pending" | "rejected";
type CalendarCouponItem = {
  id: string;
  requestedAt: string;
  approvedAt?: string | null;
  expiryDate?: string | null;
  used: boolean;
  productName: string;
  productImage: string;
  status: CouponStatus;
};
type AbsenceRequestApiItem = {
  absent_id: number;
  customer_id: number;
  customer_name?: string | null;
  booking_id: number;
  approved?: number | null;
  status?: string | null;
  approved_at?: string | null;
  created_at?: string | null;
};
type WildCardApiItem = {
  wild_card_id: number;
  customer_id: number;
  customer_name?: string | null;
  active_product_id?: number | null;
  product_name?: string | null;
  product_image?: string | null;
  absent_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  used?: number | null;
  used_date?: string | null;
  created_at?: string | null;
};

/* =================== Utils =================== */
const weekdaysShort = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const ymd = (d: Date) => { const x = new Date(d.getTime()); x.setHours(12, 0, 0, 0); return x.toISOString().slice(0, 10); };
const parseYMD = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, (m || 1) - 1, (d || 1)); };
const apiYMD = (input?: string | null) => (input ? input.slice(0, 10) : "");
const fmtDateLong = (s: string) => new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "long", year: "numeric" }).format(parseYMD(s));
const paymentStatusLabel = (status?: string | null) => {
  if (!status) return null;
  const normalized = status.toLowerCase();
  if (normalized === "paid" || normalized === "pagado") return "Pagado";
  if (normalized === "pending" || normalized === "pendiente") return "Pendiente";
  if (normalized === "cancelled" || normalized === "cancelado") return "Cancelado";
  return capitalize(status);
};
const absenceRequestWarning = "¿Seguro que quieres solicitar la ausencia? Si la solicitas no podrás usar esa sesión hasta que aprueben tu Recuperación";
const acceptedAbsenceInfo = "Se te concederá un Recuperación para que puedas disfrutar de tu sesión";
const rejectedAbsenceInfo = "Tu ausencia ha sido rechazada, no puedes acudir a esta sesión";

function normalizeAbsenceStatus(rawStatus?: string | null, approved?: number | null): CouponStatus {
  const normalized = String(rawStatus || "").toLowerCase();
  if (normalized === "approved" || Number(approved) === 1) return "granted";
  if (normalized === "rejected" || Number(approved) === 2) return "rejected";
  return "pending";
}

function isActiveAbsenceRequest(request?: Pick<AbsenceRequestApiItem, "status" | "approved"> | null) {
  if (!request) return false;
  return normalizeAbsenceStatus(request.status, request.approved) !== "rejected";
}

function getActiveAbsenceBookingIds(absenceRequests: AbsenceRequestApiItem[]) {
  const activeIds = new Set<number>();
  absenceRequests.forEach((request) => {
    if (!isActiveAbsenceRequest(request)) return;
    const bookingId = Number(request.booking_id);
    if (Number.isFinite(bookingId) && bookingId > 0) activeIds.add(bookingId);
  });
  return activeIds;
}

function initialsFromName(name?: string | null) {
  if (!name) return "–";
  const parts = name.trim().split(/\s+/);
  const f = parts[0]?.[0] ?? "";
  const l = (parts.length > 1 ? parts[parts.length - 1][0] : parts[0]?.[0]) ?? "";
  const res = (f + l).toUpperCase();
  return res || "–";
}

function normalizePhotoUrl(url?: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return url;
  return `/${url}`;
}

function normalizeProductImageUrl(image?: string | null): string | null {
  if (!image) return null;
  if (/^(https?:)?\/\//i.test(image) || image.startsWith("data:") || image.startsWith("blob:")) return image;
  if (image.startsWith("/")) return `${API_BASE}${image}`;
  return `${API_BASE}/api/product_images/${image}`;
}

function isRecoveryBookingFromRow(row: Partial<UserBookingsApiItem> | null | undefined): boolean {
  if (!row) return false;
  const toNumberOrNaN = (value: unknown) => Number(value);
  const recoverFlag = toNumberOrNaN(row.recover);
  const isRecoverFlag = toNumberOrNaN(row.is_recover);
  const recoveryFlag = toNumberOrNaN(row.recovery);
  const couponId = toNumberOrNaN(row.coupon_id);
  const wildCardId = toNumberOrNaN(row.wild_card_id);
  return (
    recoverFlag === 1 ||
    isRecoverFlag === 1 ||
    recoveryFlag === 1 ||
    (Number.isFinite(couponId) && couponId > 0) ||
    (Number.isFinite(wildCardId) && wildCardId > 0)
  );
}

function normalizeBookingsResponse(data: any) {
  const rows: UserBookingsApiItem[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray((data as any)?.bookings)
        ? (data as any).bookings
        : Array.isArray((data?.data as any)?.bookings)
          ? (data?.data as any).bookings
          : [];
  const activeBookings: ActiveBooking[] = [];
  const userBookings: UserBooking[] = [];

  rows.forEach((item) => {
    const bookingId = Number(item.booking_id ?? item.id);
    if (!Number.isFinite(bookingId)) return;

    const rawDate = String(item.start_date ?? item.date ?? "");
    const rawHour = String(item.start_time ?? item.hour ?? "");
    const normalizedHour = rawHour ? (rawHour.length === 5 ? `${rawHour}:00` : rawHour.slice(0, 8)) : "";

    activeBookings.push({
      booking_id: bookingId,
      coach_name: item.coach_name ?? item.coach ?? "",
      product_name: item.product_name ?? item.product ?? undefined,
      service_name: item.service_name ?? item.service ?? undefined,
      payment_status: item.payment_status ?? undefined,
      start_date: rawDate.slice(0, 10),
      start_time: normalizedHour,
      status: (item.status as any) || "active",
      service_id: Number(item.service_id ?? 0) || undefined,
      is_customer_late: Number(item.is_customer_late ?? 0),
      is_customer_absent: Number(item.is_customer_absent ?? 0),
    });

    userBookings.push({
      id: bookingId,
      date: rawDate.slice(0, 10),
      hour: normalizedHour,
      service_id: Number(item.service_id ?? 0),
      service: item.service ?? item.service_name ?? null,
      coach_name: item.coach_name ?? null,
      coach_profile_pic: item.coach_profile_pic ?? null,
      product_id: item.product_id ?? null,
      active_product_id: item.active_product_id ?? null,
      was_recovery: isRecoveryBookingFromRow(item),
      coupon_id: item.coupon_id ?? null,
      wild_card_id: item.wild_card_id ?? null,
    });
  });

  return { activeBookings, userBookings };
}

function buildCoachPhotoMap(bookings: UserBooking[]) {
  const photoMap: Record<number, string | null> = {};
  for (const ub of bookings) {
    photoMap[ub.id] = normalizePhotoUrl(ub.coach_profile_pic);
  }
  return photoMap;
}

async function syncLocalBookingReminders(bookings: UserBooking[], absenceRequests: AbsenceRequestApiItem[] = []) {
  if (Capacitor.getPlatform() === "web") return;
  try {
    const activeAbsenceBookingIds = getActiveAbsenceBookingIds(absenceRequests);
    const bookingRefs = bookings
      .filter(b => b?.id && b?.date && b?.hour)
      .filter(b => !activeAbsenceBookingIds.has(Number(b.id)))
      .map((b) => {
        const hour = String(b.hour ?? "");
        const hhmmss = hour.length === 5 ? `${hour}:00` : hour.slice(0, 8) || "00:00:00";
        return {
          bookingId: Number(b.id),
          serviceName: b.service ?? undefined,
          coachName: b.coach_name ?? undefined,
          startISO: `${String(b.date).slice(0, 10)}T${hhmmss}`,
        };
      })
      .filter(b => Number.isFinite(b.bookingId));
    await cancelAllBookingReminders();
    if (bookingRefs.length) {
      await scheduleManyBookingReminders(bookingRefs);
      console.log(`Recordatorios programados para ${bookingRefs.length} sesiones`);
    }
  } catch (err) {
    console.warn("Error programando recordatorios locales:", err);
  }
}

/* =================== Componente =================== */
export default function CalendarPage() {
  const today = new Date();
  const todayStr = ymd(today);
  const navigate = useNavigate();
  const location = useLocation() as { state?: { productId?: number; date?: string , activeProductId?: number } };
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

  // === Cache en memoria (no persistente) ===
  const cacheRef = useRef<{
    weeklyByKey: Map<string, WeeklyHour[]>;          // key: `${serviceId}|${dateYmd}`
    weeklyPromiseByKey: Map<string, PromiseLike<WeeklyHour[]>>;
    hourlyByKey: Map<string, HourlyCoach[]>;         // key: `${serviceId}|${date}|${timeHHMM}`
    weeklyLimitsByDate: Map<string, WeeklyLimitEntry[]>; // key: `${userId}|${targetDate}|${recoverFlag}`
    userProductsByKey: Map<string, UserProduct[]>;   // key: `${userId}|${targetDate}|${recoverFlag}`
    favoritesByKey: Map<string, number | null>;      // key: `${userId}|${primaryServiceName}`
    timeslotByHHMM: Map<string, number>;             // keys: `${sessionName}|HH:MM` and `__global__|HH:MM`
    timeslotByService: Map<number, Map<string, number>>; // serviceId -> (HH:MM -> session_timeslot_id)
    holidaysFetched: boolean;
    bookingsFetchedAt: number;
  }>(
    {
      weeklyByKey: new Map(),
      weeklyPromiseByKey: new Map(),
      hourlyByKey: new Map(),
      weeklyLimitsByDate: new Map(),
      userProductsByKey: new Map(),
      favoritesByKey: new Map(),
      timeslotByHHMM: new Map(),
      timeslotByService: new Map(),
      holidaysFetched: false,
      bookingsFetchedAt: 0,
    }
  );

  // Mes / selección
  const [currentYear, setYear] = useState<number>(today.getFullYear());
  const [currentMonth, setMonth] = useState<number>(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [activeHour, setActiveHour] = useState<string>("");

  // Usuario (para evitar leer SecureStorage muchas veces)
  const [userId, setUserId] = useState<number | null>(null);

  // VIP
  const [isVip, setIsVip] = useState<boolean>(false);

  // Datos
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [bookings, setBookings] = useState<ActiveBooking[]>([]);
  const [userBookings, setUserBookings] = useState<UserBooking[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  // primaryServices is provided by API but not used directly here; keep in cacheRef if needed.
  const [timeslots, setTimeslots] = useState<TimeslotOption[]>([]);
  const [weeklyLimits, setWeeklyLimits] = useState<WeeklyLimitEntry[]>([]);

  const [favoriteByService, setFavoriteByService] = useState<Record<number, number | null>>({});
  const [preferredCoaches, setPreferredCoaches] = useState<PreferredCoachItem[]>([]);
  const [userProducts, setUserProducts] = useState<UserProduct[]>([]);
  const [absenceRequests, setAbsenceRequests] = useState<AbsenceRequestApiItem[]>([]);
  const [wildCards, setWildCards] = useState<WildCardApiItem[]>([]);
  const [lostSessionsCount, setLostSessionsCount] = useState<number>(0);

  // Pestaña activa para reservas vs historial
  const [sessionsTab, setSessionsTab] = useState<"upcoming" | "recovery" | "past">("upcoming");
  const [recoverMode, setRecoverMode] = useState<null | number>(null);

  // Festivos
  const [holidaySet, setHolidaySet] = useState<Set<string>>(new Set());

  // Modal reservar
  const [showModal, setShowModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | "">("");
  const [selectedServiceId, setSelectedServiceId] = useState<number | "">("");
  const [timeTab, setTimeTab] = useState<"am" | "pm">("am");

  // Reprogramación
  const [rescheduleFrom, setRescheduleFrom] = useState<ActiveBooking | null>(null);

  // Horas
  const [dayHours, setDayHours] = useState<WeeklyHour[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [hourDetail, setHourDetail] = useState<{
    hour: string;
    loading: boolean;
    error: string | null;
    coaches: HourlyCoach[];
    chosenCoachId: number | null;
    manualCoachId: number | null;
  }>({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });

  const [antelacion, setAntelacion] = useState<{
    horas_antelacion_reserva: number;
    horas_antelacion_cambio: number;
    dias_antelacion_reserva: number;
  } | null>(null);

  // Cancelación
  const [cancelBusyId, setCancelBusyId] = useState<number | null>(null);
  const [absenceBusyId, setAbsenceBusyId] = useState<number | null>(null);

  // Intercambio (sin sesiones)
  const [swapCandidateId, setSwapCandidateId] = useState<number | "">("");
  const [swapCandidateDate, setSwapCandidateDate] = useState<Date | null>(null);
  const [swapBusy, setSwapBusy] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);

  // Historial pasado (modal)
  const [showPastModal, setShowPastModal] = useState(false);
  const [pastLoading, setPastLoading] = useState(false);
  const [pastError, setPastError] = useState<string | null>(null);
  const [pastSessions, setPastSessions] = useState<PastSessionDetail[]>([]);

  // Fotos (para lista de reservas)
  const [coachPhotoByBooking, setCoachPhotoByBooking] = useState<Record<number, string | null>>({});
  const [cuestionarioActivo, setCuestionarioActivo] = useState(false);
  // Primary services from API (we only need the setter in this file)
  const [, setPrimaryServices] = useState<PrimaryServiceItem[]>([]);

  // Swap mode and questionnaire state (only setters are used)
  const [, setSwapMode] = useState(false);
  const [, setPreguntaActual] = useState(0);
  const [, setRespuestas] = useState<string[]>([]);
  const [bookingIdPendiente, setBookingIdPendiente] = useState<number | null>(null);

  const [bookingBusy, setBookingBusy] = useState(false);
  const selectedDateYmd = ymd(selectedDate);

  const loadAbsenceAndWildCards = useCallback(async (uid: number, bookingsSource: UserBooking[]) => {
    if (!uid) return [];
    const bookingIds = [...new Set(bookingsSource.map((booking) => Number(booking.id)).filter((id) => Number.isFinite(id) && id > 0))];
    let parsedAbsences: AbsenceRequestApiItem[] = [];

    try {
      const response = await api.get(`/api/booking/absence-requests`);
      parsedAbsences = (Array.isArray(response.data) ? response.data : [])
        .map((row: any) => ({
          absent_id: Number(row?.absent_id ?? row?.Id ?? 0),
          customer_id: Number(row?.customer_id ?? row?.Customer_id ?? uid),
          customer_name: row?.customer_name ?? null,
          booking_id: Number(row?.booking_id ?? row?.Booking_id ?? 0),
          approved: row?.approved == null ? null : Number(row.approved),
          status: row?.status ?? null,
          approved_at: row?.approved_at ?? null,
          created_at: row?.created_at ?? null,
        }))
        .filter((row) =>
          Number.isFinite(row.absent_id) &&
          row.absent_id > 0 &&
          Number.isFinite(row.booking_id) &&
          row.booking_id > 0 &&
          (
            Number(row.customer_id) === uid ||
            bookingIds.includes(Number(row.booking_id))
          )
        );
      setAbsenceRequests(parsedAbsences);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setAbsenceRequests([]);
      } else {
        console.warn("No se pudieron cargar solicitudes de ausencia del usuario", err);
        setAbsenceRequests([]);
      }
      parsedAbsences = [];
    }

    try {
      const response = await api.get(`/api/booking/wild-cards`, { params: { customer_id: uid } });
      const cards: WildCardApiItem[] = Array.isArray(response.data)
        ? response.data.map((row: any) => ({
            wild_card_id: Number(row?.wild_card_id ?? row?.Id ?? 0),
            customer_id: Number(row?.customer_id ?? uid),
            customer_name: row?.customer_name ?? null,
            active_product_id:
              row?.active_product_id == null && row?.Active_product_id == null
                ? null
                : Number(row?.active_product_id ?? row?.Active_product_id),
            product_name: row?.product_name ?? row?.Product_name ?? null,
            product_image: row?.product_image ?? row?.Product_image ?? row?.image ?? row?.photo ?? null,
            absent_id: row?.absent_id == null ? null : Number(row.absent_id),
            start_date: row?.start_date ?? null,
            end_date: row?.end_date ?? null,
            used: row?.used == null ? 0 : Number(row.used),
            used_date: row?.used_date ?? null,
            created_at: row?.created_at ?? null,
          }))
        : [];
      setWildCards(cards.filter((card) => Number.isFinite(card.wild_card_id) && card.wild_card_id > 0));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setWildCards([]);
        return parsedAbsences;
      }
      console.warn("No se pudieron cargar comodines del usuario", err);
      setWildCards([]);
    }
    return parsedAbsences;
  }, []);

  const activeAbsenceByBooking = useMemo(
    () => getActiveAbsenceBookingIds(absenceRequests),
    [absenceRequests]
  );

  /* ====== Cuestionario pre-sesión ====== */
  useEffect(() => {
    async function checkUpcomingSession() {
      try {
        if (!userId) return;
        if (bookingIdPendiente && activeAbsenceByBooking.has(bookingIdPendiente)) {
          setBookingIdPendiente(null);
          setCuestionarioActivo(false);
          return;
        }
        const now = new Date();
        const sesionProxima = userBookings.find(s => {
          if (activeAbsenceByBooking.has(Number(s.id))) return false;
          const fechaSolo = s.date.slice(0, 10);
          const hora = s.hour.length === 5 ? `${s.hour}:00` : s.hour;
          const start = new Date(`${fechaSolo}T${hora}`);
          const diffMinutes = (start.getTime() - now.getTime()) / 60000;
          return diffMinutes >= 0 && diffMinutes <= 60; // ≤ 1h
        });

        if (sesionProxima) {
          try {
            const r = await api.get(`/api/mobile/booking-questionnaire/${sesionProxima.id}`);
            if (r.status === 404 || r.data === false) {
              setBookingIdPendiente(sesionProxima.id);
              setCuestionarioActivo(true);
              setPreguntaActual(0);
              setRespuestas([]);
            }
          } catch (err: any) {
            if (err?.response?.status === 404) {
              setBookingIdPendiente(sesionProxima.id);
              setCuestionarioActivo(true);
              setPreguntaActual(0);
              setRespuestas([]);
            }
          }
        }
      } catch (err) {
        console.error("❌ Error comprobando cuestionario:", err);
      }
    }
    if (userBookings.length > 0) checkUpcomingSession();
  }, [activeAbsenceByBooking, bookingIdPendiente, userBookings, userId]);

  async function responderPregunta(respuesta: string) {
    await enviarRespuestas([respuesta]);
  }

  async function enviarRespuestas(respuestasFinal: string[]) {
    if (!bookingIdPendiente) return;
    try {
      await api.post("/api/mobile/booking-questionnaire", {
        booking_id: bookingIdPendiente,
        estado: respuestasFinal[0]   // 👈 solo un campo
      });
      setCuestionarioActivo(false);
    } catch (err) {
      console.error("❌ Error al enviar cuestionario:", err);
    }
  }

  function omitirFormulario() {
    setCuestionarioActivo(false); // no persistimos → se repetirá en el próximo login
  }


  /* ====== Carga inicial (solo una vez) ====== */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const initialTargetDate = ymd(new Date());
        const user = await SecureStorage.getUser();
        if (!user?.id) throw new Error("Usuario no disponible. Vuelve a iniciar sesión.");
        if (!alive) return;
        setUserId(user.id);

        // VIP + datos base en paralelo
        const [
          rVip,
          rUserBookings,
          rPrimaryServices,
          rServices,
          rTimeslots,
          rUserProducts,
          rPreferredCoaches,
        ] = await Promise.allSettled([
          api.get(`/api/customers/vip/status/${user.id}`),
          api.get(`/api/mobile/user-bookings`, { params: { user_id: user.id } }),
          api.get(`/api/set-up/primary-services`),
          api.get(`/api/set-up/services`),
          api.get(`/api/set-up/session-timeslots`),
          api.get(`/api/mobile/user-products`, { params: { user_id: user.id, target_date: initialTargetDate } }),
          api.get(`/api/mobile/user/preferred-coach`, { params: { customer_id: user.id } }),
        ]);

        // VIP
        if (rVip.status === "fulfilled") {
          const { is_vip } = (rVip.value.data as { is_vip: boolean });
          setIsVip(!!is_vip);
        } else {
          setIsVip(false);
        }

        // Bookings + cache de foto
        if (rUserBookings.status === "fulfilled") {
          const { activeBookings, userBookings: normalizedUbs } = normalizeBookingsResponse(rUserBookings.value.data);
          const absenceSnapshot = await loadAbsenceAndWildCards(user.id, normalizedUbs);
          setBookings(activeBookings);
          setUserBookings(normalizedUbs);
          cacheRef.current.bookingsFetchedAt = Date.now();

          const photoMap = buildCoachPhotoMap(normalizedUbs);
          setCoachPhotoByBooking(photoMap);
          await syncLocalBookingReminders(normalizedUbs, absenceSnapshot);
        }

        // Servicios / primarios
        if (rServices.status === "fulfilled") {
          const servicesArr: ServiceItem[] = Array.isArray(rServices.value.data) ? rServices.value.data : [];
          setServices(servicesArr);
        }
        if (rPrimaryServices.status === "fulfilled") {
          const primaryArr: PrimaryServiceItem[] = Array.isArray(rPrimaryServices.value.data) ? rPrimaryServices.value.data : [];
          setPrimaryServices(primaryArr);
        }

            // Timeslots → lista + mapa O(1)
            if (rTimeslots.status === "fulfilled") {
              const raw: any[] = Array.isArray(rTimeslots.value.data) ? rTimeslots.value.data : [];
              // keep a simple timeslots array for UI if needed
              const ts: TimeslotOption[] = raw.map((t: any) => ({ id: t.session_timeslot_id, name: t.timeslot }));
              setTimeslots(ts);
              const map = cacheRef.current.timeslotByHHMM;
              map.clear();
              // Key by `${session_name}|HH:MM` and also keep a global fallback `__global__|HH:MM`
              raw.forEach(r => {
                const hhmm = String(r.timeslot).slice(0, 5);
                const sessionName = String(r.session_name || "");
                if (sessionName) map.set(`${sessionName}|${hhmm}`, r.session_timeslot_id);
                const globalKey = `__global__|${hhmm}`;
                if (!map.has(globalKey)) map.set(globalKey, r.session_timeslot_id);
              });
            }

        // Productos de usuario
        if (rUserProducts.status === "fulfilled") {
          const normalizedProducts = normalizeUserProducts(rUserProducts.value.data);
          setUserProducts(normalizedProducts);
        }

        if (rPreferredCoaches.status === "fulfilled") {
          const normalizedPreferred = normalizePreferredCoaches((rPreferredCoaches.value.data as any)?.preferred_coaches);
          setPreferredCoaches(normalizedPreferred);
        } else {
          setPreferredCoaches([]);
        }

        // Favoritos por servicio (en paralelo + cache local por nombre)
        if (rPrimaryServices.status === "fulfilled" && rPrimaryServices.value.data && Array.isArray(rPrimaryServices.value.data)) {
          const primaryArr: PrimaryServiceItem[] = rPrimaryServices.value.data;
          const favPromises = primaryArr.map(async (svc) => {
            const key = `${user.id}|${svc.name}`;
            if (cacheRef.current.favoritesByKey.has(key)) {
              return [svc.primary_service_id, cacheRef.current.favoritesByKey.get(key) ?? null] as const;
            }
            try {
              const r = await api.get("/api/mobile/user/preferred-coach-with-service", {
                params: { customer_id: user.id, service_name: svc.name }
              });
              const pref = (r.data as { preferred_coach_id?: number | null })?.preferred_coach_id ?? null;
              cacheRef.current.favoritesByKey.set(key, pref);
              return [svc.primary_service_id, pref] as const;
            } catch (err: any) {
              if (err?.response?.status !== 404) console.error("❌ Error cargando favorito:", err);
              cacheRef.current.favoritesByKey.set(key, null);
              return [svc.primary_service_id, null] as const;
            }
          });

          const favPairs = await Promise.all(favPromises);
          const favs: Record<number, number | null> = {};
          for (const [psid, pref] of favPairs) favs[psid] = pref;
          setFavoriteByService(favs);
        }

        // ❌ OJO: Ya no se cargan los weekly limits aquí; se hace en un efecto separado.

      } catch (e: any) {
        if (e?.response?.status !== 404) setError(e.message || "Error al cargar datos");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [loadAbsenceAndWildCards]);

  const hasAnyRecoveriesAvailableForSelectedDate = useMemo(() => {
    return wildCards.some((card) => {
      if (Number(card.used ?? 0) === 1) return false;
      const startYmd = apiYMD(card.start_date ?? null);
      const endYmd = apiYMD(card.end_date ?? null);
      if (startYmd && selectedDateYmd < startYmd) return false;
      if (endYmd && selectedDateYmd > endYmd) return false;
      return true;
    });
  }, [selectedDateYmd, wildCards]);

  const shouldRequestRecoveryContext = sessionsTab === "recovery" || !!recoverMode || hasAnyRecoveriesAvailableForSelectedDate;

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) return;

      const recoverFlag = shouldRequestRecoveryContext ? 1 : 0;
      const key = `${userId}|${selectedDateYmd}|${recoverFlag}`;

      if (cacheRef.current.userProductsByKey.has(key)) {
        if (alive) setUserProducts(cacheRef.current.userProductsByKey.get(key)!);
        return;
      }

      try {
        const params: Record<string, string | number> = {
          user_id: userId,
          target_date: selectedDateYmd,
        };
        if (recoverFlag) params.recover = 1;

        const response = await api.get(`/api/mobile/user-products`, { params });
        const normalizedProducts = normalizeUserProducts(response.data);
        cacheRef.current.userProductsByKey.set(key, normalizedProducts);
        if (alive) setUserProducts(normalizedProducts);
      } catch (err: any) {
        if (!alive) return;
        if (err?.response?.status !== 404) {
          console.error("❌ Error cargando productos contextuales:", err);
        }
        setUserProducts([]);
      }
    })();

    return () => { alive = false; };
  }, [selectedDateYmd, shouldRequestRecoveryContext, userId]);

  // Abrir modal directo con producto (desde navegación con state)
  useEffect(() => {
    // Support both { productId } (legacy) and { activeProductId } (preferred when available)
    const pid = location?.state?.productId;
    const apid = (location as any)?.state?.activeProductId;
    const incoming = apid ?? pid;
    if (!incoming) return;

    // If incoming is an active_product_id, use it directly. Otherwise try to
    // resolve to the active_product_id for the product, falling back to the pid.
    const asActive = userProducts.find(p => p.active_product_id === incoming || p.id === incoming);
    if (asActive) setSelectedProductId(asActive.active_product_id ?? asActive.id);
    else setSelectedProductId(incoming);

    setShowModal(true);
    // Clear navigation state so it doesn't re-trigger when using back/replace
    navigate(".", { replace: true, state: {} });
  }, [location?.state, navigate, userProducts]);

  /* Festivos — una sola llamada */
  useEffect(() => {
    (async () => {
      try {
        if (cacheRef.current.holidaysFetched) return;
        const r = await api.get(`/api/booking/holidays`);
        const dates = Array.isArray(r.data) ? r.data : [];
        const set = new Set<string>(dates.map((d: any) => apiYMD(d?.date ?? d)).filter(Boolean));
        setHolidaySet(set);
        cacheRef.current.holidaysFetched = true;
      } catch (err: any) {
        if (err?.response?.status === 404) return; // sin festivos
        console.error("❌ Error cargando festivos:", err);
      }
    })();
  }, []);

  /* Refrescar reservas al cambiar de mes — throttle (evita golpear server por cada click) */
  useEffect(() => {
    (async () => {
      try {
        const now = Date.now();
        if (!userId) return;
        // 5 minutos de frescura
        const STALE_MS = 5 * 60 * 1000;
        if (now - cacheRef.current.bookingsFetchedAt < STALE_MS) return;

        const r = await api.get(`/api/mobile/user-bookings`, { params: { user_id: userId } });
        const { activeBookings, userBookings: normalizedUbs } = normalizeBookingsResponse(r.data);
        const absenceSnapshot = await loadAbsenceAndWildCards(userId, normalizedUbs);
        setBookings(activeBookings);
        setUserBookings(normalizedUbs);
        setCoachPhotoByBooking(buildCoachPhotoMap(normalizedUbs));
        cacheRef.current.bookingsFetchedAt = now;
        await syncLocalBookingReminders(normalizedUbs, absenceSnapshot);
      } catch (e) {
        // silencioso
      }
    })();
  }, [currentMonth, currentYear, userId, loadAbsenceAndWildCards]);

  /* Al cambiar producto: seleccionar automáticamente servicio único y cargar horas (usando cache) */
  const servicesForProduct = useMemo<ServiceItem[]>(() => {
    if (!selectedProductId) return [];
    const sel = userProducts.find(p => p.id === selectedProductId || p.active_product_id === selectedProductId);
    if (!sel) return [];

    const allowedServiceIds = sel.serviceIds ?? [];
    if (!allowedServiceIds.length) return [];

    const userIsVip = isVip;

    let filtered = services.filter(s => {
      const matchesProduct = allowedServiceIds.includes(Number(s.service_id));
      if (!matchesProduct) return false;

      // Usuarios VIP pueden acceder a cualquier centro de los servicios asociados
      if (userIsVip) return true;

      const productCentro = String(sel?.centro || "").trim().toLowerCase();
      const serviceCentro = String(s.centro || "").trim().toLowerCase();

      // Si el usuario no es VIP, no puede reservar servicios marcados como VIP.
      // Un producto con centro "vip" se trata como "sin centro fijado" para no bloquear
      // productos históricos que guardaron ese valor por defecto.
      if (serviceCentro === "vip") return false;

      // Si el producto tiene centro específico, debe coincidir con el servicio
      if (productCentro && productCentro !== "vip") {
        return serviceCentro === productCentro;
      }
      return true;
    });




    // Excepción de “Sotillo sábado → incluir 2 Guadarrama”
    if (
      selectedProductId === 41 &&
      selectedProduct?.centro?.toLowerCase() === "sotillo" &&
      (() => {
        if (!selectedDate) return false;
        const d = selectedDate instanceof Date ? selectedDate : parseYMD(String(selectedDate));
        return d.getDay() === 6; // sábado
      })()
    ) {
      const exists = filtered.some(s => s.service_id === 2);
      if (!exists) {
        const guadarrama =
          services.find(s => s.service_id === 2 && (s.service_name || "").toUpperCase().includes("GUADARRAMA")) ||
          { service_id: 2, service_name: "ENTRENAMIENTO GUADARRAMA", centro: "guadarrama" };
        filtered = [...filtered, guadarrama];
      }
    }

    return filtered;
  }, [selectedProductId, services, userProducts, selectedDate, isVip]);

  const bookingMetaById = useMemo(() => {
    const map = new Map<number, {
      product_id: number | null;
      active_product_id: number | null;
      service_id: number | null;
      was_recovery: boolean;
      coupon_id: number | null;
      wild_card_id: number | null;
    }>();
    for (const ub of userBookings) {
      map.set(ub.id, {
        product_id: ub.product_id ?? null,
        active_product_id: ub.active_product_id ?? null,
        service_id: ub.service_id ?? null,
        was_recovery: Boolean(ub.was_recovery),
        coupon_id: ub.coupon_id ?? null,
        wild_card_id: ub.wild_card_id ?? null,
      });
    }
    return map;
  }, [userBookings]);

  const wasRecoveryBooking = useCallback((bookingId: number) => {
    const meta = bookingMetaById.get(bookingId);
    if (!meta) return false;
    return (
      Boolean(meta.was_recovery) ||
      (Number.isFinite(Number(meta.coupon_id)) && Number(meta.coupon_id) > 0) ||
      (Number.isFinite(Number(meta.wild_card_id)) && Number(meta.wild_card_id) > 0)
    );
  }, [bookingMetaById]);

  const isAprendeBooking = useCallback(
    (bookingId: number, booking?: ActiveBooking) => {
      const meta = bookingMetaById.get(bookingId);
      const baseId = resolveBaseProductId(meta?.active_product_id ?? meta?.product_id ?? null, userProducts);
      if (baseId === APRENDE_PRODUCT_ID) return true;
      const name = String(booking?.product_name || "");
      return name.toLowerCase().includes("aprende");
    },
    [bookingMetaById, userProducts]
  );

  useEffect(() => {
    (async () => {
      if (!selectedProductId) {
        setSelectedServiceId("");
        setAntelacion(null);
        // no vaciamos weeklyLimits aquí: se comparten entre productos en esa fecha
        return;
      }

      if (rescheduleFrom) {
        try {
          const rAnt = await api.get(`/api/set-up/product-from-active_products/${selectedActiveProductId}`);
          if (rAnt.data && typeof rAnt.data === "object") {
            const antData = rAnt.data as {
              horas_antelacion_reserva?: number | string;
              horas_antelacion_cambio?: number | string;
              dias_antelacion_reserva?: number | string;
            };
            setAntelacion({
              horas_antelacion_reserva: Number(antData.horas_antelacion_reserva) || 0,
              horas_antelacion_cambio: Number(antData.horas_antelacion_cambio) || 0,
              dias_antelacion_reserva: Number(antData.dias_antelacion_reserva) || 0,
            });
          } else {
            setAntelacion(null);
          }
        } catch {
          setAntelacion(null);
        }

        // Si ya hay servicio fijado, precargar horas del día seleccionado
        if (selectedServiceId) {
          await fetchWeeklyForDay(Number(selectedServiceId), ymd(selectedDate));
        }
        // No seguir con la lógica de auto-selección/limpieza
        return;
      }
      // Si solo hay un servicio, auto-seleccionarlo y precargar horas
      if (servicesForProduct.length === 1) {
        const onlyService = servicesForProduct[0];
        if (selectedServiceId !== onlyService.service_id) {
          setSelectedServiceId(onlyService.service_id);
          setActiveHour("");
          await fetchWeeklyForDay(onlyService.service_id, ymd(selectedDate)); // cacheado
        }
      } else if (!servicesForProduct.find(s => s.service_id === selectedServiceId)) {
        setSelectedServiceId("");
        setDayHours([]);
        setActiveHour("");
      }

      // Antelación del producto
      try {
        const rAnt = await api.get(`/api/set-up/product-from-active_products/${selectedActiveProductId}`);
        if (rAnt.data && typeof rAnt.data === "object") {
          const antData = rAnt.data as {
            horas_antelacion_reserva?: number | string;
            horas_antelacion_cambio?: number | string;
            dias_antelacion_reserva?: number | string;
          };
          setAntelacion({
            horas_antelacion_reserva: Number(antData.horas_antelacion_reserva) || 0,
            horas_antelacion_cambio: Number(antData.horas_antelacion_cambio) || 0,
            dias_antelacion_reserva: Number(antData.dias_antelacion_reserva) || 0,
          });
        } else {
          setAntelacion(null);
        }
      } catch (err) {
        console.error("❌ Error cargando antelación:", err);
        setAntelacion(null);
      }

      // ⛔️ Eliminado: el fetch de weekly limits estaba duplicado aquí.

    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, servicesForProduct, selectedServiceId, selectedDate, userId, rescheduleFrom]);

  // Weekly limit — depende solo de la fecha seleccionada (y userId)
  useEffect(() => {
    (async () => {
      try {
        if (!userId) return;
        const recoverFlag = shouldRequestRecoveryContext ? 1 : 0;
        const key = `${userId}|${selectedDateYmd}|${recoverFlag}`;
        if (cacheRef.current.weeklyLimitsByDate.has(key)) {
          setWeeklyLimits(cacheRef.current.weeklyLimitsByDate.get(key)!);
        } else {
          const params: Record<string, string | number> = {
            user_id: userId,
            target_date: selectedDateYmd,
          };
          if (recoverFlag) params.recover = 1;
          const rWeekly = await api.get(`/api/mobile/user-weekly-limit`, {
            params,
          });
          const weeklyData = (rWeekly.data as { weekly_limits?: WeeklyLimitEntry[] })?.weekly_limits ?? [];
          cacheRef.current.weeklyLimitsByDate.set(key, weeklyData);
          setWeeklyLimits(weeklyData);
        }
      } catch (err: any) {
        if (err?.response?.status !== 404) console.error("❌ Error cargando límites:", err);
        setWeeklyLimits([]);
      }
    })();
  }, [selectedDateYmd, shouldRequestRecoveryContext, userId]);

  // Auto-mapa HH:MM -> timeslot_id cuando cambian timeslots (seguridad extra)
  useEffect(() => {
    const map = cacheRef.current.timeslotByHHMM;
    map.clear();
    timeslots.forEach(t => map.set(String(t.name).slice(0, 5), t.id));
  }, [timeslots]);

  // Cuando cambia el servicio seleccionado, cargar timeslots específicos de ese servicio
  useEffect(() => {
    async function loadForService() {
      if (!selectedServiceId) return;
      const sid = Number(selectedServiceId);
      if (cacheRef.current.timeslotByService.has(sid)) return;
      try {
        const r = await api.get(`/api/set-up/session-timeslots/service/${sid}`);
        const rows: any[] = Array.isArray(r.data) ? r.data : [];
        const svcMap = new Map<string, number>();
        rows.forEach(rr => {
          const hh = String(rr.timeslot).slice(0, 5);
          svcMap.set(hh, rr.session_timeslot_id);
          // also ensure global fallback map contains at least one id for hh
          const globalKey = `__global__|${hh}`;
          if (!cacheRef.current.timeslotByHHMM.has(globalKey)) cacheRef.current.timeslotByHHMM.set(globalKey, rr.session_timeslot_id);
        });
        cacheRef.current.timeslotByService.set(sid, svcMap);
      } catch (err: any) {
        console.warn("No se pudieron cargar timeslots por servicio:", err?.message || err);
      }
    }
    loadForService();
  }, [selectedServiceId]);

  /* =================== Cálculos =================== */
  const datesWithSessions = useMemo(() => {
    const set = new Set<string>();
    bookings.forEach(b => { if (b.status === "active") { const d = apiYMD(b.start_date); if (d) set.add(d); } });
    return set;
  }, [bookings]);

  const allReservedSorted = useMemo(() => {
    const toIso = (b: ActiveBooking) => `${apiYMD(b.start_date)}T${(b.start_time || "00:00:00").slice(0, 8)}`;
    return bookings
      .filter(b => b.status === "active")
      .slice()
      .sort((a, b) => toIso(a).localeCompare(toIso(b)));
  }, [bookings]);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(new Date(currentYear, currentMonth, 1)),
    [currentYear, currentMonth]
  );

  const firstWeekdayOffset = (y: number, m: number) => (new Date(y, m, 1).getDay() + 6) % 7; // Lunes=0
  const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
  const goPrevMonth = useCallback(() => { const d = new Date(currentYear, currentMonth - 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }, [currentYear, currentMonth]);
  const goNextMonth = useCallback(() => { const d = new Date(currentYear, currentMonth + 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }, [currentYear, currentMonth]);

  const selectedActiveProductId = useMemo(() => {
  const found = userProducts.find(p => p.id === selectedProductId || p.active_product_id === selectedProductId);
    return found?.active_product_id ?? null;
  }, [selectedProductId, userProducts]);

  const selectedBaseProductId = useMemo(
    () => resolveBaseProductId(selectedProductId, userProducts),
    [selectedProductId, userProducts]
  );
  const isAprendeSelectedProduct = selectedBaseProductId === APRENDE_PRODUCT_ID;


  /* Crédito restante real usando /user-weekly-limit */
  // Determine the weekly-limit entry that corresponds to the currently selected product.
  // Because the backend can return multiple entries with the same product_id but
  // different active_product_id, we try several fallbacks to find the correct one:
  // 1) match by active_product_id === selectedProductId
  // 2) match by active_product_id === selectedActiveProductId (if we derived it)
  // 3) match by product_id === selectedProductId
  // The resulting `serviceCredit` is built from that chosen weeklyLimits entry.
  const serviceCredit = useMemo(() => {
    if (!selectedProductId || weeklyLimits.length === 0) return null;

    // try direct match: maybe selectedProductId already is an active_product_id
    let entry = weeklyLimits.find(w => w.active_product_id === selectedProductId);

    // fallback: if we derived an active id from userProducts, try that
    if (!entry && selectedActiveProductId != null) {
      entry = weeklyLimits.find(w => w.active_product_id === selectedActiveProductId);
    }

    // final fallback: match by base product_id (less specific)
    if (!entry) {
      entry = weeklyLimits.find(w => w.product_id === selectedProductId);
    }

    if (!entry) return null;

    if (entry.type_of_product === "recurrent") {
      return {
        mode: "weekly" as const,
        type: entry.type_of_product,
        remaining: entry.remaining,
        limit: entry.weekly_limit ?? 0,
        used: entry.used,
        _matchedEntry: entry, // attach for other logic to reuse
      } as any;
    } else {
      return {
        mode: "total" as const,
        type: entry.type_of_product,
        remaining: entry.remaining,
        limit: entry.total_limit ?? 0,
        used: entry.used,
        _matchedEntry: entry,
      } as any;
    }
  }, [selectedProductId, selectedActiveProductId, weeklyLimits]);

  /* Candidatas para intercambio según el modo de crédito */
  const swapCandidates = useMemo(() => {
    if (!serviceCredit || !selectedProductId) return [];
    const todayIso = todayStr;

    const toLabel = (b: ActiveBooking) =>
      `${apiYMD(b.start_date)} · ${(b.start_time || "").slice(0, 5)} — ${b.service_name} (${b.coach_name || "Coach"})`;

    const notTooSoon = (b: ActiveBooking) =>
      !!b.start_time && hoursUntil(apiYMD(b.start_date), b.start_time) >= 12;

    const sameProduct = (b: ActiveBooking) => {
      const meta = bookingMetaById.get(b.booking_id);
      if (!meta) return false;
      const metaIds = [meta.product_id, meta.active_product_id].filter((id): id is number => id != null);
      if (metaIds.length === 0) return false;
      // If we found a matched weeklyLimits entry, compare against both its
      // product_id and active_product_id. Otherwise, try both comparisons
      // with selectedProductId/selectedActiveProductId as a last resort.
      const matched = (serviceCredit as any)?._matchedEntry as WeeklyLimitEntry | undefined;
      if (matched) {
        return metaIds.some(id => id === matched.product_id || id === matched.active_product_id);
      }
      if (selectedActiveProductId != null) {
        return metaIds.some(id => id === selectedActiveProductId || id === selectedProductId);
      }
      return metaIds.some(id => id === selectedProductId);
    };

    return allReservedSorted
      .filter((b) => apiYMD(b.start_date) >= todayIso)
      .filter(notTooSoon)
      .filter(sameProduct)
      .map((b) => ({ id: b.booking_id, label: toLabel(b), start_date: b.start_date }));
  }, [serviceCredit, allReservedSorted, todayStr, selectedProductId, selectedActiveProductId, bookingMetaById]);


  // NOTE: missedSessions removed (unused). Recreate from weeklyLimits if needed.

  /* ====== Acciones ====== */
  const onDayClick = useCallback(async (dateStr?: string, disabled?: boolean) => {
    if (!dateStr || disabled) return;

    const isPast = dateStr < todayStr;
    const hadSessions = datesWithSessions.has(dateStr);

    setSelectedDate(parseYMD(dateStr));
    setActiveHour(""); // evita que quede marcada una hora de otro día

    // Histórico en días pasados
    if (isPast && hadSessions) {
      setShowPastModal(true);
      setPastLoading(true);
      setPastError(null);
      try {
        const sameDay = bookings
          .filter(b => apiYMD(b.start_date) === dateStr && b.status === "active");
        const details: PastSessionDetail[] = sameDay.map(b => ({
          booking_id: b.booking_id,
          product_name: b.product_name,
          service_name: b.service_name,
          coach_name: b.coach_name,
          date: dateStr,
          time: b.start_time ? b.start_time.slice(0, 5) : undefined,
          payment_status: b.payment_status,
          comment: null,
          is_customer_late: Number(b.is_customer_late ?? 0),
          is_customer_absent: Number(b.is_customer_absent ?? 0),
        }));
        setPastSessions(details);
      } catch (e: any) {
        setPastError(e.message || "No se pudo cargar el historial del día");
      } finally {
        setPastLoading(false);
      }
      return;
    }

    // Modal de reserva (hoy/futuro)
    setShowModal(true);
    setRescheduleFrom(null);
    setSwapMode(false);
    setSwapCandidateId("");
    setSwapError(null);

    setSelectedProductId("");
    setSelectedServiceId("");
    setDayHours([]);
    setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });
  }, [todayStr, datesWithSessions, bookings]);

  // === PRODUCTOS y SERVICIOS por producto ===
  // Resolve selected product by product `id` or its `active_product_id`.
  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    return userProducts.find(p => p.id === selectedProductId || p.active_product_id === selectedProductId) || null;
  }, [selectedProductId, userProducts]);

  const isSelectedRecurrentProduct = useMemo(
    () => String(selectedProduct?.type_of_product ?? "").toLowerCase() === "recurrent",
    [selectedProduct]
  );

  /**
   * Las suscripciones recurrentes solo pueden tener una sesión diaria del
   * mismo servicio. No se compara el nombre (por ejemplo, "Entrenamiento"),
   * porque productos de servicios distintos pueden compartir esa palabra y
   * deben poder reservarse el mismo día.
   */
  const hasRecurrentBookingForSelectedServiceOnDate = useCallback((date: Date, excludedBookingId?: number) => {
    if (!isSelectedRecurrentProduct || !selectedServiceId) return false;

    const targetDate = ymd(date);
    return userBookings.some((booking) =>
      booking.id !== excludedBookingId &&
      apiYMD(booking.date) === targetDate &&
      Number(booking.service_id) === Number(selectedServiceId)
    );
  }, [isSelectedRecurrentProduct, selectedServiceId, userBookings]);

  const hasActiveRecurrentProduct = useMemo(() => {
    return weeklyLimits.some((entry) => {
      if (String(entry?.type_of_product ?? "").toLowerCase() !== "recurrent") return false;
      if (typeof entry?.eligible_for_target_date === "boolean") return entry.eligible_for_target_date;
      return true;
    });
  }, [weeklyLimits]);

  useEffect(() => {
    if (!hasActiveRecurrentProduct && sessionsTab === "recovery") {
      setSessionsTab("upcoming");
    }
  }, [hasActiveRecurrentProduct, sessionsTab]);

  const pendingPaymentRestriction = useMemo(() => {
    if (!selectedProduct) return null;
    const status = String(selectedProduct.payment_status ?? "").toLowerCase();
    if (status !== "pending" && status !== "pendiente") return null;
    const purchaseYmd = apiYMD(selectedProduct.purchase_date);
    if (!purchaseYmd) return null;
    const purchaseDate = parseYMD(purchaseYmd);
    purchaseDate.setDate(purchaseDate.getDate());
    return {
      purchaseYmd,
      minSelectableYmd: ymd(purchaseDate),
    };
  }, [selectedProduct]);

  useEffect(() => {
    if (!pendingPaymentRestriction) return;
    const currentYmd = ymd(selectedDate);
    if (currentYmd > pendingPaymentRestriction.purchaseYmd) return;

    let candidate = parseYMD(pendingPaymentRestriction.minSelectableYmd);
    let guard = 0;
    while (guard < 60 && !isSelectableDate(candidate)) {
      const next = new Date(candidate.getTime());
      next.setDate(next.getDate() + 1);
      candidate = next;
      guard += 1;
    }

    if (guard >= 60) return;
    const candidateYmd = ymd(candidate);
    if (candidateYmd === currentYmd) return;

    setSelectedDate(candidate);
    setDayHours([]);
    setActiveHour("");
    if (selectedServiceId) {
      fetchWeeklyForDay(Number(selectedServiceId), candidateYmd);
    }
  }, [pendingPaymentRestriction, selectedDate, selectedServiceId]);

  // allowedServiceIds available via selectedProduct.serviceIds when needed

  // === Data fetching cacheado ===
  async function fetchWeeklyForDay(serviceId: number, dateStr: string) {
    const key = `${serviceId}|${dateStr}`;
    try {
      setSlotsLoading(true); setSlotsError(null);
      let all: WeeklyHour[] | undefined;
      if (cacheRef.current.weeklyByKey.has(key)) {
        all = cacheRef.current.weeklyByKey.get(key);
      } else {
        let pending = cacheRef.current.weeklyPromiseByKey.get(key);
        if (!pending) {
          pending = api.get(`/api/booking/weekly`, {
            params: { service_id: serviceId, date: dateStr, single_day: 1 }
          }).then(res => Array.isArray(res.data) ? res.data : []);
          cacheRef.current.weeklyPromiseByKey.set(key, pending);
        }
        all = await pending;
        cacheRef.current.weeklyPromiseByKey.delete(key);
        cacheRef.current.weeklyByKey.set(key, all);
      }

      const filtered = (all ?? [])
        .filter(x => x.date === dateStr)
        .map(x => ({
          ...x,
          free: Math.max(0, (x.capacity ?? 0) - (x.booked ?? 0)),
          isFull: (x.booked ?? 0) >= (x.capacity ?? 0)
        }))
        .sort((a, b) => a.hour.localeCompare(b.hour));
// 1) Pre-filtramos con /hourly → sólo dejar horas con datos (>0)
      const okSet = await prefilterHoursWithHourly(serviceId, dateStr, filtered.map(x => x.hour));

      // 2) Reconstruimos dayHours únicamente con las HH:MM válidas
      const filteredByHourly = filtered
        .filter(x => okSet.has(x.hour.slice(0, 5)))
        .sort((a, b) => a.hour.localeCompare(b.hour));

      setDayHours(filteredByHourly);
      setActiveHour("");
      const first = filtered[0]?.hour?.slice(0, 2);
      if (first) setTimeTab(Number(first) < 12 ? "am" : "pm");
    } catch (e: any) {
      cacheRef.current.weeklyPromiseByKey.delete(key);
      setSlotsError(e.message || "Error al consultar disponibilidad");
    } finally { setSlotsLoading(false); }
  }

  // Prefiltra horas consultando /api/booking/hourly para cada HH:MM.
// Devuelve un Set con las HH:MM que SÍ tienen datos (length > 0).
async function prefilterHoursWithHourly(serviceId: number, dateStr: string, hours: string[]) {
  const okHHmm = new Set<string>();

  // Usamos la misma bandera de carga de slots
  setSlotsLoading(true);
  setSlotsError(null);

  try {
    const promises = hours.map(async (h) => {
      const hhmm = h.slice(0, 5);
      const key = `${serviceId}|${dateStr}|${hhmm}`;
      let data: HourlyCoach[];

      // cache
      if (cacheRef.current.hourlyByKey.has(key)) {
        data = cacheRef.current.hourlyByKey.get(key)!;
      } else {
        const r = await api.get(`/api/booking/hourly`, { params: { service_id: serviceId, date: dateStr, time: hhmm } });
        data = Array.isArray(r.data) ? r.data : [];
        cacheRef.current.hourlyByKey.set(key, data);
      }

      if (Array.isArray(data) && data.length > 0) {
        okHHmm.add(hhmm);
      }
    });

    await Promise.all(promises);
    return okHHmm;
  } catch (err: any) {
    setSlotsError(err?.message || "Error al consultar horarios");
    return okHHmm;
  } finally {
    setSlotsLoading(false);
  }
}


  async function loadHourDetail(hourHHMMSS: string) {
    if (!selectedServiceId) return;
    const hhmm = hourHHMMSS.slice(0, 5);
    setHourDetail({ hour: hhmm, loading: true, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });
    try {
            const key = `${selectedServiceId}|${ymd(selectedDate)}|${hhmm}`;
      let data: HourlyCoach[];

      // 👉 Intentar LEER de cache (ya llenada en el prefiltro)
      if (cacheRef.current.hourlyByKey.has(key)) {
        data = cacheRef.current.hourlyByKey.get(key)!;
      } else {
        // Fallback por seguridad (no debería ocurrir salvo navegación rara)
        const r = await api.get(`/api/booking/hourly`, {
          params: { service_id: selectedServiceId, date: ymd(selectedDate), time: hhmm }
        });
        data = Array.isArray(r.data) ? r.data : [];
        cacheRef.current.hourlyByKey.set(key, data);
      }


      const withFree = data.filter(c => (c.capacity ?? 0) - (c.booked ?? 0) > 0);

      const requireManual =
        isPhysioService(Number(selectedServiceId)) ||
        isVipManualEligible(Number(selectedServiceId), services, isVip);

      const isTraining = isTrainingService(Number(selectedServiceId), services);
      const prioritizedFavId = pickPreferredCoach(withFree, preferredCoaches, isTraining);
      const legacyFavId = !isTraining ? (favoriteByService[Number(selectedServiceId)] ?? null) : null;
      const preferredCoachId = prioritizedFavId ?? legacyFavId;

      let chosenId: number | null = null;
      let manualId: number | null = null;

      if (preferredCoachId && withFree.some(c => c.coach_id === preferredCoachId)) {
        if (requireManual) manualId = preferredCoachId;
        else chosenId = preferredCoachId;
      } else if (withFree.length > 0) {
        const fallbackId = withFree[0].coach_id;
        if (requireManual) manualId = fallbackId;
        else chosenId = fallbackId;
      }

      setHourDetail({
        hour: hhmm,
        loading: false,
        error: null,
        coaches: withFree,
        chosenCoachId: chosenId,
        manualCoachId: manualId,
      });
    } catch (e: any) {
      setHourDetail({ hour: "", loading: false, error: e.message || "Error al cargar detalle", coaches: [], chosenCoachId: null, manualCoachId: null });
    }
  }

  /* ====== Crear / Cancelar / Intercambiar / Reprogramar ====== */
  async function getTimeslotIdForHour(hhmm: string, serviceId?: number) {
    const normalizedHour = String(hhmm).slice(0, 5);
    if (serviceId) {
      const normalizedServiceId = Number(serviceId);
      let serviceMap = cacheRef.current.timeslotByService.get(normalizedServiceId);
      if (!serviceMap) {
        try {
          const response = await api.get(`/api/set-up/session-timeslots/service/${normalizedServiceId}`);
          const rows: any[] = Array.isArray(response.data) ? response.data : [];
          serviceMap = new Map<string, number>();
          rows.forEach(row => {
            serviceMap!.set(String(row.timeslot).slice(0, 5), row.session_timeslot_id);
          });
          cacheRef.current.timeslotByService.set(normalizedServiceId, serviceMap);
        } catch (err: any) {
          console.warn("No se pudo resolver el horario para el servicio:", err?.message || err);
          return null;
        }
      }
      return serviceMap?.get(normalizedHour) ?? null;
    }

    const map = cacheRef.current.timeslotByHHMM;
    const globalKey = `__global__|${normalizedHour}`;
    return map.get(globalKey) ?? null;
  }

  async function ensureUserId() {
    if (userId) return userId;
    const user = await SecureStorage.getUser();
    if (!user?.id) throw new Error("Usuario no disponible.");
    setUserId(user.id);
    return user.id;
  }

  // Valida si un producto activo puede usarse en la fecha seleccionada
  // Busca exclusivamente por active_product_id (no usar product.id)
  function canBookWithProduct(
    activeProductId: number | "",
    options?: { isRecoveryAttempt?: boolean }
  ): { ok: boolean; message?: string } {
    if (!activeProductId) return { ok: true };
    const wanted = Number(activeProductId);
    const prod: any = userProducts.find(p => Number(p.active_product_id) === wanted);
    if (!prod) return { ok: true };
    const expiryIso = prod.expiry_date ?? prod.expiryDate ?? null;
    const subscriptionStatus = prod.subscription_status ?? prod.subscriptionStatus ?? null;
    const typeOfProduct = prod.type_of_product ?? prod.typeOfProduct ?? null;
    if (!expiryIso) return { ok: true };
    const expiryYmd = apiYMD(String(expiryIso));
    const targetYmd = selectedDateYmd;
    const isRecoveryAttempt = Boolean(options?.isRecoveryAttempt);
    const targetEligible = typeof prod.eligible_for_target_date === "boolean" ? prod.eligible_for_target_date : null;
    const recoveryEligible = typeof prod.recovery_eligible_for_target_date === "boolean" ? prod.recovery_eligible_for_target_date : null;
    const hasRecoveryWildCard = Boolean(selectedProductWildCard?.wild_card_id);

    if (!isRecoveryAttempt && expiryYmd && targetYmd > expiryYmd) {
      if (String(typeOfProduct).toLowerCase() === "recurrent") {
        return { ok: false, message: `No puedes reservar: este producto expiró el ${fmtDateLong(expiryYmd)} para la fecha seleccionada.` };
      }
      return { ok: false, message: `No puedes reservar: el bono expiró el ${fmtDateLong(expiryYmd)}.` };
    }

    if (targetEligible === true) return { ok: true };
    if (targetEligible === false && recoveryEligible === true && !isRecoveryAttempt && !hasRecoveryWildCard) {
      return { ok: false, message: "Este producto solo puede usarse como recuperación y no tienes una recuperación válida para esa fecha." };
    }

    // Recurrent: si subscription_status === 'canceled' y fecha de reserva > expiry_date => bloquear
    if (String(typeOfProduct).toLowerCase() === "recurrent") {
      if (String(subscriptionStatus).toLowerCase() === "canceled" && expiryYmd && targetYmd > expiryYmd) {
        return { ok: false, message: `No puedes reservar: la suscripción está cancelada y expira el ${fmtDateLong(expiryYmd)}.` };
      }
      if (recoveryEligible === true && (hasRecoveryWildCard || isRecoveryAttempt)) {
        return { ok: true };
      }
      if (targetEligible === false && expiryYmd && targetYmd > expiryYmd) {
        return { ok: false, message: `No puedes reservar: este producto expiró el ${fmtDateLong(expiryYmd)} para la fecha seleccionada.` };
      }
      return { ok: true };
    }

    // Para bonos u otros tipos: si expiry_date < fecha intento reserva => bloquear
    if (expiryYmd && targetYmd > expiryYmd) {
      return { ok: false, message: `No puedes reservar: el bono expiró el ${fmtDateLong(expiryYmd)}.` };
    }
    return { ok: true };
  }

  async function createBooking() {

    if (bookingBusy) return;
    setBookingBusy(true);

    if (!selectedProductId) {
      alert("Selecciona un producto.");
      setBookingBusy(false);
      return;
    }
    if (!selectedServiceId) {
      alert("Selecciona un servicio.");
      setBookingBusy(false);
      return;
    }
    if (!hourDetail.hour) {
      alert("Selecciona una hora.");
      setBookingBusy(false);
      return;
    }

    const uid = await ensureUserId();
    const hasNoRemainingSessions = Boolean(
      serviceCredit &&
      Number.isFinite(serviceCredit.remaining) &&
      serviceCredit.remaining === 0
    );
    const isRecurrentProduct = String(serviceCredit?.type ?? selectedProduct?.type_of_product ?? "").toLowerCase() === "recurrent";
    const selectedRecoveryWildCard =
      !rescheduleFrom && hasNoRemainingSessions && isRecurrentProduct
        ? (selectedProductWildCard ?? null)
        : null;
    const isRecoveryAttempt = Boolean(selectedRecoveryWildCard?.wild_card_id);

    if (!rescheduleFrom && hasNoRemainingSessions && !selectedRecoveryWildCard) {
      alert(
        isRecurrentProduct
          ? "Has alcanzado el límite semanal y no tienes un Recuperación asociado a este producto."
          : "No te quedan sesiones disponibles para este producto."
      );
      setBookingBusy(false);
      return;
    }

    // Validar producto/fecha antes de resolver timeslot
    const canBook = canBookWithProduct(selectedActiveProductId ?? "", { isRecoveryAttempt });
    if (!canBook.ok) {
      alert(canBook.message || "No puedes reservar con este producto en esa fecha.");
      setBookingBusy(false);
      return;
    }

    const session_timeslot_id = await getTimeslotIdForHour(hourDetail.hour, Number(selectedServiceId));
    if (!session_timeslot_id) {
      alert("No se encontró el ID del horario para el servicio seleccionado.");
      setBookingBusy(false);
      return;
    }
    const alreadyHasBooking = bookings.some(b => {
      const sameDate = apiYMD(b.start_date) === ymd(selectedDate);
      const sameHour = b.start_time?.slice(0, 5) === hourDetail.hour;
      const sameService = Number(b.service_id) === Number(selectedServiceId);
      return b.status === "active" && sameDate && sameHour && sameService;
    });

    if (alreadyHasBooking) {
      alert("Ya tienes una reserva de este servicio a esta hora.");
      setBookingBusy(false);
      return;
    }

    if (!rescheduleFrom && hasRecurrentBookingForSelectedServiceOnDate(selectedDate)) {
      alert("Ya tienes una reserva de este servicio para este producto recurrente. No puedes reservar dos sesiones del mismo servicio el mismo día.");
      setBookingBusy(false);
      return;
    }

    const slotDateTime = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
    if (diffHours < 1) {
      alert("No se puede reservar con menos de 1 hora de antelación.");
      setBookingBusy(false);
      return;
    }

    try {
      if (rescheduleFrom) {
        return await rescheduleByCancelCreate(rescheduleFrom.booking_id, uid, session_timeslot_id);
      } else {
        const payload = {
          active_product_id: selectedActiveProductId,
          product_id: selectedProductId,
          customer_id: uid,
          coach_id: hourDetail.manualCoachId ?? hourDetail.chosenCoachId ?? undefined,
          service_id: selectedServiceId,
          session_timeslot_id,
          start_date: ymd(selectedDate),
          amount: 0, discount: 0, total_amount: 0,
          coupon_id: null, payment_method: "cash",
          bank_account_id: null, card_id: null,
        };
        if (selectedRecoveryWildCard?.wild_card_id) {
          (payload as any).recover = 1;
        }
        const res = await api.post(`/api/coaches/create-booking`, payload);
        const hasBookingId = res.data && typeof res.data === "object" && "booking_id" in res.data;
        const hasSuccess = res.data && typeof res.data === "object" && "success" in res.data && (res.data as any).success;
        if (!hasSuccess && !hasBookingId) throw new Error((res.data as any)?.error || "No se pudo crear la reserva");

        if (isAprendeSelectedProduct) {
          const bookingId = extractBookingId(res.data);
          await createAprendeTask({
            bookingId,
            coachId: payload.coach_id ?? null,
            startDateYmd: payload.start_date,
          });
        }

        await refreshAfterMutation(uid);
        resetAndCloseModal();
        alert("Reserva creada correctamente.");
        setBookingBusy(false);
      }
    } catch (e: any) {
      alert(e.message || "Error al procesar la reserva");
      setBookingBusy(false);
    }
  }

  // recoverSession removed - not used in current UI. Recreate if recovery flow
  // needs to be re-enabled.

  async function cancelLocalBookingReminder(bookingId: number) {
    if (Capacitor.getPlatform() === "web") return;
    try {
      await cancelBookingReminder(bookingId);
    } catch (err) {
      console.warn("No se pudo cancelar el recordatorio local:", err);
    }
  }

  async function rescheduleByCancelCreate(oldBookingId: number, userIdLocal: number, session_timeslot_id: number) {
    setBookingBusy(true)
    const slotDateTime = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
    if (diffHours < 12) {
      return alert("No se puede cambiar a una hora con menos de 12 horas de antelación.");
    }

    if (!session_timeslot_id) return alert("No se encontró el ID del horario.");
    // Validación producto/fecha para reprogramar
    const isRecoveryReschedule = wasRecoveryBooking(oldBookingId);
    const canReschedule = canBookWithProduct(selectedActiveProductId ?? selectedProductId, {
      isRecoveryAttempt: isRecoveryReschedule
    });
    if (!canReschedule.ok) {
      alert(canReschedule.message || "No puedes reprogramar a esta fecha con ese producto.");
      setBookingBusy(false);
      return;
    }
    const alreadyHasBooking = bookings.some(b => {
      const sameDate = apiYMD(b.start_date) === ymd(selectedDate);
      const sameHour = b.start_time?.slice(0, 5) === hourDetail.hour;
      const sameService = Number(b.service_id) === Number(selectedServiceId);
      return b.status === "active" && sameDate && sameHour && sameService;
    });

    if (alreadyHasBooking) {
      alert("Ya tienes una reserva de este servicio a esta hora.");
      setBookingBusy(false);
      return;
    }

    if (hasRecurrentBookingForSelectedServiceOnDate(selectedDate, oldBookingId)) {
      alert("Ya tienes una reserva de este servicio para este producto recurrente. No puedes reservar dos sesiones del mismo servicio el mismo día.");
      return;
    }

    try {
      await api.delete(`/api/mobile/booking/${oldBookingId}`);
      await cancelLocalBookingReminder(oldBookingId);
      const oldBooking = bookings.find(b => b.booking_id === oldBookingId) || rescheduleFrom || undefined;
      if (isAprendeBooking(oldBookingId, oldBooking)) {
        await deleteAprendeTask({
          bookingId: oldBookingId,
          startDateYmd: apiYMD(oldBooking?.start_date),
        });
      }
      const payload = {
        active_product_id: selectedActiveProductId,
        product_id: selectedProductId,
        customer_id: userIdLocal,
        coach_id: hourDetail.manualCoachId ?? hourDetail.chosenCoachId ?? undefined,
        service_id: selectedServiceId,
        session_timeslot_id,
        start_date: ymd(selectedDate),
        amount: 0, discount: 0, total_amount: 0,
        coupon_id: null, payment_method: "cash",
        bank_account_id: null, card_id: null,
      };
      if (Number(payload.coupon_id) > 0 || wasRecoveryBooking(oldBookingId)) {
        (payload as any).recover = 1;
      }
      const res = await api.post(`/api/coaches/create-booking`, payload);
      const ok = !!((res.data as any)?.success || (res.data as any)?.booking_id);
      if (!ok) throw new Error((res.data as any)?.error || (res.data as any)?.message || "No se pudo crear la reserva");

      if (isAprendeSelectedProduct) {
        const bookingId = extractBookingId(res.data);
        await createAprendeTask({
          bookingId,
          coachId: payload.coach_id ?? null,
          startDateYmd: payload.start_date,
        });
      }

      await refreshAfterMutation(userIdLocal);
      resetAndCloseModal();
      alert("Hora cambiada correctamente.");
    } catch (e: any) {
      alert(e?.message || "No se pudo completar el cambio de hora.");
    }
    finally{
      setBookingBusy(false);
    }
  }

  async function cancelReservation(bookingId: number) {
  const booking = bookings.find(b => b.booking_id === bookingId);
  if (!booking) {
    alert("No se encontró la reserva.");
    return;
  }

  const ok = window.confirm("¿Seguro que quieres cancelar esta reserva?");
  if (!ok) return;

  setCancelBusyId(bookingId);

  try {
    const uid = await ensureUserId();

    await api.delete(`/api/mobile/booking/${bookingId}`);

    await cancelLocalBookingReminder(bookingId);
    if (isAprendeBooking(bookingId, booking)) {
      await deleteAprendeTask({
        bookingId,
        startDateYmd: apiYMD(booking.start_date),
      });
    }
    await refreshAfterMutation(uid);

    alert("Reserva cancelada correctamente.");
  } catch (e: any) {
    // ✅ Capturar el mensaje del backend (400/404/etc)
    const backendMsg =
      e?.response?.data?.error ||
      e?.response?.data?.message ||
      e?.message ||
      "No se pudo cancelar la reserva";

    alert(backendMsg);
  } finally {
    setCancelBusyId(null);
  }
}

  async function requestAbsenceForBooking(bookingId: number) {
    if (pendingAbsenceByBooking.has(bookingId)) {
      alert("Ya tienes una solicitud de ausencia pendiente para esta reserva.");
      return;
    }
    setAbsenceBusyId(bookingId);
    try {
      const uid = await ensureUserId();
      const response = await api.post(`/api/booking/absence-requests/${bookingId}`, {
        customer_id: uid,
      });
      try {
        await refreshAfterMutation(uid);
      } catch (refreshErr) {
        console.warn("No se pudo refrescar la vista tras solicitar la ausencia:", refreshErr);
      }
      if (bookingIdPendiente === bookingId) {
        setBookingIdPendiente(null);
        setCuestionarioActivo(false);
      }
      const backendWarning = (response?.data as { warning?: string | null } | undefined)?.warning;
      alert(backendWarning || absenceRequestWarning);
    } catch (e: any) {
      const backendMsg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "No se pudo solicitar la ausencia";
      alert(backendMsg);
    } finally {
      setAbsenceBusyId(null);
    }
  }


  async function swapAndCreate() {
    if (!swapCandidateId) return;
    if (!selectedProductId) return alert("Selecciona un producto."); 
    if (!selectedServiceId) return alert("Selecciona un servicio.");
    if (!hourDetail.hour) return alert("Selecciona una hora.");

    const uid = await ensureUserId();
    // Validar producto/fecha antes de proceder con swap
    const isRecoverySwap = wasRecoveryBooking(Number(swapCandidateId));
    const canSwap = canBookWithProduct(selectedActiveProductId ?? selectedProductId, {
      isRecoveryAttempt: isRecoverySwap
    });
    if (!canSwap.ok) {
      alert(canSwap.message || "No puedes mover/cambiar a esa fecha con ese producto.");
      return;
    }

    const session_timeslot_id = await getTimeslotIdForHour(hourDetail.hour, Number(selectedServiceId));
    if (!session_timeslot_id) return alert("No se encontró el ID del horario para el servicio seleccionado.");

    const slotDateTime = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
    if (diffHours < 12) {
      setSwapError("No se puede cambiar a una hora con menos de 12 horas de antelación.");
      return;
    }

    const alreadyHasBooking = bookings.some(b => {
      const sameDate = apiYMD(b.start_date) === ymd(selectedDate);
      const sameHour = b.start_time?.slice(0, 5) === hourDetail.hour;
      const sameService = Number(b.service_id) === Number(selectedServiceId);
      return b.status === "active" && sameDate && sameHour && sameService;
    });

    if (alreadyHasBooking) {
      alert("Ya tienes una reserva de este servicio a esta hora.");
      setBookingBusy(false);
      return;
    }

    if (hasRecurrentBookingForSelectedServiceOnDate(selectedDate, Number(swapCandidateId))) {
      alert("Ya tienes otra reserva de este servicio ese día. No puedes mover la seleccionada ahí.");
      return;
    }

    function isSameWeek(d1: Date, d2: Date) {
      const monday = (date: Date) => {
        const d = new Date(date);
        const day = d.getDay() || 7;
        d.setDate(d.getDate() - day + 1);
        d.setHours(0, 0, 0, 0);
        return d;
      };
      return monday(d1).getTime() === monday(d2).getTime();
    }

    const newDate = new Date(`${ymd(selectedDate)}T${hourDetail.hour}:00`);
    const limitReached = serviceCredit?.remaining === 0;

    if (swapCandidateDate && !isSameWeek(swapCandidateDate, newDate) && limitReached) {
      alert("No puedes mover una sesión de otra semana a esta, porque ya alcanzaste el límite semanal.");
      return;
    }

    setSwapBusy(true);
    setSwapError(null);
    try {
      const swapId = Number(swapCandidateId);
      await api.delete(`/api/mobile/booking/${swapCandidateId}`);
      await cancelLocalBookingReminder(Number(swapCandidateId));
      const swapBooking = bookings.find(b => b.booking_id === swapId);
      if (swapId && isAprendeBooking(swapId, swapBooking)) {
        await deleteAprendeTask({
          bookingId: swapId,
          startDateYmd: apiYMD(swapBooking?.start_date),
        });
      }

      const payload = {
        active_product_id: selectedActiveProductId,
        product_id: selectedProductId,
        customer_id: uid,
        coach_id: hourDetail.manualCoachId ?? hourDetail.chosenCoachId ?? undefined,
        service_id: selectedServiceId,
        session_timeslot_id,
        start_date: ymd(selectedDate),
        amount: 0,
        discount: 0,
        total_amount: 0,
        coupon_id: null,
        payment_method: "cash",
        bank_account_id: null,
        card_id: null,
      };
      if (Number(payload.coupon_id) > 0 || wasRecoveryBooking(swapId)) {
        (payload as any).recover = 1;
      }
      const res = await api.post(`/api/coaches/create-booking`, payload);
      const ok = !!((res.data as any)?.success || (res.data as any)?.booking_id);
      if (!ok) throw new Error((res.data as any)?.error || (res.data as any)?.message || "No se pudo crear la reserva");

      if (isAprendeSelectedProduct) {
        const bookingId = extractBookingId(res.data);
        await createAprendeTask({
          bookingId,
          coachId: payload.coach_id ?? null,
          startDateYmd: payload.start_date,
        });
      }

      await refreshAfterMutation(uid);
      resetAndCloseModal();
      alert("Reserva cambiada correctamente.");
    } catch (e: any) {
      setSwapError(e?.response?.data?.error || e?.message || "No se pudo completar el cambio.");
    } finally {
      setSwapBusy(false);
    }
  }

  async function refreshAfterMutation(uid: number) {
    const [rUB, rLim, rUP] = await Promise.all([
      api.get(`/api/mobile/user-bookings`, { params: { user_id: uid } }),
      api.get(`/api/mobile/user-weekly-limit`, {
        params: {
          user_id: uid,
          target_date: selectedDateYmd,
          ...(shouldRequestRecoveryContext ? { recover: 1 } : {}),
        },
      }),
      api.get(`/api/mobile/user-products`, {
        params: {
          user_id: uid,
          target_date: selectedDateYmd,
          ...(shouldRequestRecoveryContext ? { recover: 1 } : {}),
        },
      }),
    ]);

    const { activeBookings, userBookings: normalizedUbs } = normalizeBookingsResponse(rUB.data);
    const absenceSnapshot = await loadAbsenceAndWildCards(uid, normalizedUbs);
    setBookings(activeBookings);
    cacheRef.current.bookingsFetchedAt = Date.now();
    cacheRef.current.weeklyByKey.clear();
    cacheRef.current.weeklyPromiseByKey.clear();
    cacheRef.current.hourlyByKey.clear();

    setUserBookings(normalizedUbs);
    setCoachPhotoByBooking(buildCoachPhotoMap(normalizedUbs));
    await syncLocalBookingReminders(normalizedUbs, absenceSnapshot);

    if (rLim.data && typeof rLim.data === "object" && "weekly_limits" in rLim.data) {
      const limits = (rLim.data as { weekly_limits: WeeklyLimitEntry[] }).weekly_limits;
      const key = `${uid}|${selectedDateYmd}|${shouldRequestRecoveryContext ? 1 : 0}`;
      cacheRef.current.weeklyLimitsByDate.clear();
      cacheRef.current.weeklyLimitsByDate.set(key, limits);
      setWeeklyLimits(limits);
    }

    const normalizedProducts = normalizeUserProducts(rUP.data);
    const productsKey = `${uid}|${selectedDateYmd}|${shouldRequestRecoveryContext ? 1 : 0}`;
    cacheRef.current.userProductsByKey.clear();
    cacheRef.current.userProductsByKey.set(productsKey, normalizedProducts);
    setUserProducts(normalizedProducts);
  }

  function resetAndCloseModal() {
    setSwapMode(false);
    setSwapCandidateId("");
    setSwapError(null);
    setShowModal(false);
    setRescheduleFrom(null);
    setSelectedProductId("");
    setSelectedServiceId("");
    setRecoverMode(null);
    setDayHours([]);
    setActiveHour("");
    setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });
    setBookingBusy(false);
  }

  // NUEVO: abrir modal de reprogramación con producto/servicio bloqueados
  function openReschedule(b: ActiveBooking) {
    setRescheduleFrom(b);
    setSwapMode(false);
    setSwapCandidateId("");
    setSwapError(null);

    const dateIso = apiYMD(b.start_date);
    if (dateIso) setSelectedDate(parseYMD(dateIso));
    setSwapCandidateId(b.booking_id);

    // Obtener metadatos fiables desde /api/mobile/user-bookings
    const meta = bookingMetaById.get(b.booking_id);
    const activeProdFromMeta = meta?.active_product_id ?? null;
    const prodIdFromMeta = meta?.product_id ?? null;
    const svcIdFromMeta  = meta?.service_id ?? null;

    if (activeProdFromMeta) {
      setSelectedProductId(activeProdFromMeta);
    } else if (prodIdFromMeta) {
      setSelectedProductId(prodIdFromMeta);
    } else {
      // Fallback por nombre (por si faltara product_id)
      const prod = userProducts.find((p) => p.name === b.product_name);
      if (prod) setSelectedProductId(prod.id);
    }

    if (svcIdFromMeta) {
      setSelectedServiceId(svcIdFromMeta);
    } else {
      // Fallback por nombre de servicio
      const svc = services.find((s) => s.service_name === b.service_name);
      if (svc) setSelectedServiceId(svc.service_id);
    }

    // Reset de estado de horas y detalle
    setDayHours([]);
    setActiveHour("");
    setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null, manualCoachId: null });

    const finalSvcId =
      svcIdFromMeta ??
      services.find((s) => s.service_name === b.service_name)?.service_id ??
      null;

    // Precargar horas del día original para ese servicio
    if (finalSvcId && dateIso) {
      fetchWeeklyForDay(finalSvcId, dateIso);
    }

    setShowModal(true);
  }

  // Límites y validaciones para el <input type="date">
  const dayToday = today.getDate();
  function lastDayOf(y: number, m: number) { return new Date(y, m + 1, 0); }
  const allowNextMonth = dayToday >= 15;

  const baseMaxSelectableDate = (() => {
    // VIP ya no tiene ventana extra; se aplica la misma regla general.
    // if (isVip) {
    //   const target = new Date(today.getFullYear(), today.getMonth() + 3, 1);
    //   return lastDayOf(target.getFullYear(), target.getMonth());
    // }
    const target = allowNextMonth ? new Date(today.getFullYear(), today.getMonth() + 1, 1) : today;
    return lastDayOf(target.getFullYear(), target.getMonth());
  })();

  // Máximo seleccionable combinando la regla base y la antelación del producto
  const maxSelectableDate = (() => {
    const diaLimite = antelacion?.dias_antelacion_reserva ?? 0;
    if (diaLimite > 0) {
      const limitByDaysAhead = new Date(today.getTime());
      limitByDaysAhead.setHours(12, 0, 0, 0);
      limitByDaysAhead.setDate(limitByDaysAhead.getDate() + diaLimite);
      return limitByDaysAhead.getTime() > baseMaxSelectableDate.getTime()
        ? limitByDaysAhead
        : baseMaxSelectableDate;
    }
    return baseMaxSelectableDate;
  })();

  function isSelectableDate(d: Date) {
    const iso = ymd(d);
    if (iso < todayStr) return false;
    if (pendingPaymentRestriction && iso <= pendingPaymentRestriction.purchaseYmd) return false;
    if (iso > ymd(maxSelectableDate)) return false;
    if (d.getDay() === 0) return false; // domingos
    if (holidaySet.has(iso)) return false;

    // VIP ya no omite el lÃ­mite por mes; se aplica a todos.
    //if (!isVip) {
    const sameMonth =
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth();
    const isNextMonth =
      d.getFullYear() === nextMonth.getFullYear() &&
      d.getMonth() === nextMonth.getMonth();
    if (!sameMonth && !(allowNextMonth && isNextMonth)) return false;
    return true;
  }

  /* =================== Render =================== */
  const offset = firstWeekdayOffset(currentYear, currentMonth);
  const totalDays = daysInMonth(currentYear, currentMonth);
  const cells: Array<{ day?: number; date?: string }> = [];
  for (let i = 0; i < offset; i++) cells.push({});
  for (let d = 1; d <= totalDays; d++) {
    const ds = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, date: ds });
  }
  const selectedYmd = ymd(selectedDate);
  const nowMadrid = new Date();
  const todayMadridStr = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(nowMadrid);
  const nowMadridHHMM = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Madrid",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(nowMadrid);

  const isBookingUpcoming = useCallback((booking: ActiveBooking) => {
    const reservaDate = apiYMD(booking.start_date);
    if (!reservaDate) return false;
    if (reservaDate < todayMadridStr) return false;
    if (reservaDate === todayMadridStr && booking.start_time) {
      const reservaHHMM = booking.start_time.slice(0, 5);
      const [startHourRaw, startMinuteRaw] = reservaHHMM.split(":").map(part => Number(part));
      const [nowHourRaw, nowMinuteRaw] = nowMadridHHMM.split(":").map(part => Number(part));

      if (
        Number.isFinite(startHourRaw) &&
        Number.isFinite(startMinuteRaw) &&
        Number.isFinite(nowHourRaw) &&
        Number.isFinite(nowMinuteRaw)
      ) {
        const startTotalMinutes = startHourRaw * 60 + startMinuteRaw;
        const nowTotalMinutes = nowHourRaw * 60 + nowMinuteRaw;
        if (nowTotalMinutes > startTotalMinutes + 60) return false;
      }
    }
    return true;
  }, [todayMadridStr, nowMadridHHMM]);

  const upcomingBookings = useMemo(
    () => allReservedSorted.filter(isBookingUpcoming),
    [allReservedSorted, isBookingUpcoming]
  );

  const pastBookings = useMemo(() => {
    const filtered = allReservedSorted.filter(b => !isBookingUpcoming(b));
    return filtered.reverse();
  }, [allReservedSorted, isBookingUpcoming]);

  const upcomingCount = upcomingBookings.length;
  const bookingById = useMemo(() => {
    const map = new Map<number, ActiveBooking>();
    bookings.forEach((booking) => {
      if (Number.isFinite(booking.booking_id)) map.set(Number(booking.booking_id), booking);
    });
    return map;
  }, [bookings]);
  const pendingAbsenceByBooking = useMemo(() => {
    const pendingSet = new Set<number>();
    absenceRequests.forEach((request) => {
      if (normalizeAbsenceStatus(request.status, request.approved) === "pending") {
        const bookingId = Number(request.booking_id);
        if (Number.isFinite(bookingId) && bookingId > 0) pendingSet.add(bookingId);
      }
      
    });
    return pendingSet;
  }, [absenceRequests]);
  const acceptedAbsenceByBooking = useMemo(() => {
    const acceptedSet = new Set<number>();
    absenceRequests.forEach((request) => {
      if (normalizeAbsenceStatus(request.status, request.approved) === "granted") {
        const bookingId = Number(request.booking_id);
        if (Number.isFinite(bookingId) && bookingId > 0) acceptedSet.add(bookingId);
      }
    });
    return acceptedSet;
  }, [absenceRequests]);
  const rejectedAbsenceByBooking = useMemo(() => {
    const rejectedSet = new Set<number>();
    absenceRequests.forEach((request) => {
      if (normalizeAbsenceStatus(request.status, request.approved) === "rejected") {
        const bookingId = Number(request.booking_id);
        if (Number.isFinite(bookingId) && bookingId > 0) rejectedSet.add(bookingId);
      }
    });
    return rejectedSet;
  }, [absenceRequests]);
  const calendarCoupons = useMemo<CalendarCouponItem[]>(() => {
    const items: CalendarCouponItem[] = [];
    const absenceById = new Map<number, AbsenceRequestApiItem>();

    absenceRequests.forEach((request) => {
      const absentId = Number(request.absent_id);
      if (Number.isFinite(absentId) && absentId > 0) absenceById.set(absentId, request);
    });

    wildCards.forEach((card) => {
      const wildCardId = Number(card.wild_card_id);
      if (!Number.isFinite(wildCardId) || wildCardId <= 0) return;
      const absentIdNum = Number(card.absent_id);
      const hasAbsentId = Number.isFinite(absentIdNum) && absentIdNum > 0;
      const linkedAbsence = hasAbsentId ? absenceById.get(absentIdNum) : null;
      const linkedBooking = linkedAbsence ? bookingById.get(Number(linkedAbsence.booking_id)) : undefined;
      const requestedAt = apiYMD(linkedAbsence?.created_at ?? card.created_at ?? card.start_date ?? todayStr) || todayStr;
      const approvedAt = apiYMD(linkedAbsence?.approved_at ?? card.created_at ?? null) || null;
      const expiryDate = apiYMD(card.end_date ?? null) || null;
      if (!expiryDate || expiryDate < todayStr) return;

      const activeProductId = Number(card.active_product_id);
      const hasActiveProductId = Number.isFinite(activeProductId) && activeProductId > 0;
      const linkedProduct = hasActiveProductId
        ? userProducts.find((product) => Number(product.active_product_id ?? product.id) === activeProductId)
        : null;
      const normalizedProductImage = normalizeProductImageUrl(
        card.product_image ??
        linkedProduct?.image ??
        null
      );

      items.push({
        id: `wc-${wildCardId}`,
        requestedAt,
        approvedAt,
        expiryDate,
        used: Number(card.used ?? 0) === 1,
        productName:
          card.product_name ||
          linkedProduct?.name ||
          linkedBooking?.product_name ||
          "Producto asociado",
        productImage: normalizedProductImage || exerciseImage,
        status: "granted",
      });
    });

    items.sort((a, b) => {
      const aKey = `${a.requestedAt}T00:00:00`;
      const bKey = `${b.requestedAt}T00:00:00`;
      return bKey.localeCompare(aKey);
    });
    return items;
  }, [absenceRequests, wildCards, bookingById, todayStr, userProducts]);
  const nonExpiredCoupons = useMemo(
    () => calendarCoupons,
    [calendarCoupons]
  );
  const currentMonthKey = todayStr.slice(0, 7);
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) {
        if (alive) setLostSessionsCount(0);
        return;
      }
      try {
        const response = await api.get(`/api/booking/sessions/lost/monthly`, {
          params: { customer_id: userId, month: currentMonthKey },
        });
        const totalLost = Number(
          (response?.data as { total_lost_sessions?: number; totalLostSessions?: number })?.total_lost_sessions ??
          (response?.data as { total_lost_sessions?: number; totalLostSessions?: number })?.totalLostSessions ??
          0
        );
        if (alive) setLostSessionsCount(Number.isFinite(totalLost) && totalLost > 0 ? totalLost : 0);
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          console.warn("No se pudieron cargar las sesiones perdidas mensuales", err);
        }
        if (alive) setLostSessionsCount(0);
      }
    })();
    return () => { alive = false; };
  }, [userId, currentMonthKey]);

  const monthlyAbsenceRequestsCount = useMemo(
    () =>
      absenceRequests.reduce((total, request) => {
        const createdYmd = apiYMD(request.created_at ?? null);
        if (!createdYmd) return total;
        return createdYmd.slice(0, 7) === currentMonthKey ? total + 1 : total;
      }, 0),
    [absenceRequests, currentMonthKey]
  );
  const totalAbsenceRequestQuota = useMemo(() => {
    const seen = new Set<number>();
    return weeklyLimits.reduce((total, entry) => {
      if (String(entry?.type_of_product || "").toLowerCase() !== "recurrent") return total;

      const key = Number(entry?.active_product_id ?? entry?.product_id ?? 0);
      if (Number.isFinite(key) && key > 0) {
        if (seen.has(key)) return total;
        seen.add(key);
      }

      const limit = Number(entry?.weekly_limit ?? 0);
      if (!Number.isFinite(limit) || limit <= 0) return total;
      return total + limit;
    }, 0);
  }, [weeklyLimits]);
  const availableRecoveriesCount = useMemo(
    () => nonExpiredCoupons.filter((coupon) => !coupon.used).length,
    [nonExpiredCoupons]
  );
  const availableWildCardsForSelectedProduct = useMemo<WildCardApiItem[]>(() => {
    if (!selectedProductId) return [];

    const selectedIds = [selectedProductId, selectedActiveProductId, selectedBaseProductId]
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const selectedProductName = String(selectedProduct?.name ?? "").trim().toLowerCase();

    const absenceById = new Map<number, AbsenceRequestApiItem>();
    absenceRequests.forEach((request) => {
      const absentId = Number(request.absent_id);
      if (Number.isFinite(absentId) && absentId > 0) absenceById.set(absentId, request);
    });

    return wildCards.filter((card) => {
      if (Number(card.used ?? 0) === 1) return false;
      const startYmd = apiYMD(card.start_date ?? null);
      const expiryYmd = apiYMD(card.end_date ?? null);
      if (startYmd && selectedDateYmd < startYmd) return false;
      if (expiryYmd && selectedDateYmd > expiryYmd) return false;

      const cardActiveProductId = Number(card.active_product_id);
      if (Number.isFinite(cardActiveProductId) && cardActiveProductId > 0 && selectedIds.includes(cardActiveProductId)) {
        return true;
      }

      const absentId = Number(card.absent_id);
      if (!Number.isFinite(absentId) || absentId <= 0) return false;
      const linkedAbsence = absenceById.get(absentId);
      if (!linkedAbsence) return false;

      const bookingId = Number(linkedAbsence.booking_id);
      if (!Number.isFinite(bookingId) || bookingId <= 0) return false;

      const meta = bookingMetaById.get(bookingId);
      if (meta) {
        const metaBaseId = resolveBaseProductId(meta.active_product_id ?? meta.product_id ?? null, userProducts);
        const metaIds = [meta.active_product_id, meta.product_id, metaBaseId]
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0);
        if (selectedIds.some((id) => metaIds.includes(id))) return true;
      }

      const linkedProductName = String(bookingById.get(bookingId)?.product_name ?? "").trim().toLowerCase();
      return Boolean(selectedProductName && linkedProductName && linkedProductName === selectedProductName);
    });
  }, [
    selectedProductId,
    selectedActiveProductId,
    selectedBaseProductId,
    selectedProduct,
    absenceRequests,
    wildCards,
    bookingMetaById,
    bookingById,
    userProducts,
    selectedDateYmd,
  ]);
  const isSelectedRecurrentProductForRecovery = String(serviceCredit?.type ?? selectedProduct?.type_of_product ?? "").toLowerCase() === "recurrent";
  const selectedProductWildCard = availableWildCardsForSelectedProduct[0] ?? null;
  const canReserveWithWildCard = Boolean(
    !rescheduleFrom &&
    isSelectedRecurrentProductForRecovery &&
    serviceCredit &&
    Number.isFinite(serviceCredit.remaining) &&
    serviceCredit.remaining === 0 &&
    selectedProductWildCard
  );
  const shouldForceSwapFlow = Boolean(
    !rescheduleFrom &&
    selectedServiceId &&
    serviceCredit &&
    Number.isFinite(serviceCredit.remaining) &&
    serviceCredit.remaining === 0 &&
    !canReserveWithWildCard
  );
  
  const diaMax = maxSelectableDate;
  const minSelectableDateYmd =
    pendingPaymentRestriction && pendingPaymentRestriction.minSelectableYmd > todayStr
      ? pendingPaymentRestriction.minSelectableYmd
      : todayStr;

  return (
    <div style={{ position: "relative", minHeight: "100vh", paddingBottom: 60 }}>
      <div style={{ padding: "12px" }}>
        {/* Encabezado calendario */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <button type="button" onClick={goPrevMonth} style={navBtnStyle} aria-label="Mes anterior">‹</button>
          <div style={{ fontWeight: 700 }}>{capitalize(monthLabel)}</div>
          <button type="button" onClick={goNextMonth} style={navBtnStyle} aria-label="Mes siguiente">›</button>
        </div>

        {/* Cabecera semana */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
          {weekdaysShort.map(w => (
            <div key={`wd-${w}`} style={{ textAlign: "center", fontSize: 12, color: "#6b7280" }}>{w}</div>
          ))}
        </div>

        {/* Grilla días */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {cells.map((c, idx) => {
            const isSelected = c.date === selectedYmd;
            const isReserved = !!(c.date && datesWithSessions.has(c.date));
            const isToday = c.date === todayStr;
            const isPast = !!(c.date && c.date < todayStr);
            const isHoliday = !!(c.date && holidaySet.has(c.date));
            const isSunday = !!(c.date && parseYMD(c.date).getDay() === 0);

            const dateObj = c.date ? parseYMD(c.date) : null;
            let canSelect = false;

            const hasReservation = !!(c.date && datesWithSessions.has(c.date));
            const pendingBlocked = Boolean(
              pendingPaymentRestriction &&
              c.date &&
              !hasReservation &&
              c.date <= pendingPaymentRestriction.purchaseYmd
            );

            if (dateObj) {
              if (hasReservation) {
                canSelect = true;
              } else if (isVip) {
                canSelect = ymd(dateObj) <= ymd(maxSelectableDate);
              } else {
                const sameMonth =
                  dateObj.getFullYear() === today.getFullYear() &&
                  dateObj.getMonth() === today.getMonth();
                const isNextMonth2 =
                  dateObj.getFullYear() === nextMonth.getFullYear() &&
                  dateObj.getMonth() === nextMonth.getMonth();
                const after15 = today.getDate() >= 15;
                if (sameMonth) canSelect = true;
                else if (isNextMonth2 && after15) canSelect = true;
              }
            }

            const disabled =
              !c.day ||
              isHoliday ||
              isSunday ||
              (!hasReservation && isPast) ||
              !canSelect ||
              pendingBlocked;

            const dayTitle =
              pendingBlocked && pendingPaymentRestriction
                ? `Disponible a partir del ${fmtDateLong(pendingPaymentRestriction.minSelectableYmd)}`
                : undefined;

            return (
              <button
                type="button"
                key={`day-${c.date || idx}`}
                disabled={disabled}
                onClick={() => onDayClick(c.date, disabled)}
                title={dayTitle}
                style={{
                  ...dayCellStyle,
                  ...(isReserved ? dayCellReservedStyle : {}),
                  ...(isHoliday || isSunday ? dayCellHolidayStyle : {}),
                  ...(isSelected ? dayCellSelectedStyle : {}),
                  ...(isToday ? dayCellTodayStyle : {}),
                  opacity: c.day ? (disabled ? 0.45 : 1) : 0,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                {c.day || ""}
              </button>
            );
          })}
        </div>

        {/* Mensajes top */}
        {loading && <p style={{ marginTop: 12 }}>Cargando…</p>}
        {error && <p style={{ color: "red", marginTop: 12 }}>{error}</p>}

        {/* ===== Módulos de sesiones ===== */}
        {!loading && !error && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginTop: 12 }}>
              <div style={{ ...tabBar, margin: "0 0 12px", gridTemplateColumns: "repeat(3, 1fr)" }}>
                <button
                  type="button"
                  onClick={() => setSessionsTab("upcoming")}
                  style={{ ...tabBtn, ...(sessionsTab === "upcoming" ? tabBtnActive : {}) }}
                >
                  Sesiones Futuras
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!hasActiveRecurrentProduct) {
                      alert("esta funcion solo está disponible para usuarios con productos recurrentes contratados");
                      return;
                    }
                    setSessionsTab("recovery");
                  }}
                  style={{
                    ...tabBtn,
                    ...(!hasActiveRecurrentProduct ? tabBtnDisabled : {}),
                    ...(sessionsTab === "recovery" && hasActiveRecurrentProduct ? tabBtnActive : {}),
                  }}
                  aria-disabled={!hasActiveRecurrentProduct}
                  title={!hasActiveRecurrentProduct ? "Esta función solo está disponible para usuarios con productos recurrentes contratados" : ""}
                >
                  Recuperación de Sesiones
                </button>
                <button
                  type="button"
                  onClick={() => setSessionsTab("past")}
                  style={{ ...tabBtn, ...(sessionsTab === "past" ? tabBtnActive : {}) }}
                >
                  Sesiones Pasadas
                </button>
              </div>

              {sessionsTab !== "recovery" && (
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 16 }}>
                    {sessionsTab === "upcoming" ? "Tus sesiones reservadas" : "Historial de sesiones"}
                  </h3>
                  <span style={{ color: "#6b7280" }}>
                    — {sessionsTab === "upcoming" ? `${upcomingCount} próximas` : `${pastBookings.length} registradas`}
                  </span>
                </div>
              )}

              {sessionsTab === "upcoming" ? (
                upcomingCount === 0 ? (
                  <p style={{ color: "#6b7280" }}>No tienes sesiones reservadas.</p>
                ) : (
                  upcomingBookings.map(b => {
                    const photo = coachPhotoByBooking[b.booking_id] ?? null;
                    const minHorasCambio = antelacion?.horas_antelacion_cambio ?? 0;
                    const hoursToStart = hoursUntil(apiYMD(b.start_date), b.start_time);
                    const canReschedule = hoursToStart >= minHorasCambio;
                    const bookingMeta = bookingMetaById.get(b.booking_id);
                    const trainingById = bookingMeta?.service_id
                      ? isTrainingService(bookingMeta.service_id, services)
                      : false;
                    const trainingByName = !trainingById && isTrainingServiceName(b.service_name);
                    const isTrainingBooking = trainingById || trainingByName;
                    const hideCoachInfo = !isVip && isTrainingBooking && hoursToStart > 1;
                    const coachLabel = hideCoachInfo ? "Se asignará 1 hora antes" : (b.coach_name || "—");
                    const bookingProductType = (() => {
                      const matchedByActive = userProducts.find((product) =>
                        bookingMeta?.active_product_id != null && product.active_product_id === bookingMeta.active_product_id
                      );
                      if (matchedByActive?.type_of_product) return matchedByActive.type_of_product;

                      const matchedByProduct = userProducts.find((product) =>
                        bookingMeta?.product_id != null &&
                        (product.id === bookingMeta.product_id || product.active_product_id === bookingMeta.product_id)
                      );
                      if (matchedByProduct?.type_of_product) return matchedByProduct.type_of_product;

                      const baseId = resolveBaseProductId(bookingMeta?.active_product_id ?? bookingMeta?.product_id ?? null, userProducts);
                      const matchedByBase = userProducts.find((product) => product.id === baseId);
                      return matchedByBase?.type_of_product ?? null;
                    })();
                    const isRecurrentBooking = String(bookingProductType || "").toLowerCase() === "recurrent";
                    const requiresAbsenceRequest = 1 < hoursToStart  && hoursToStart < 12 && isRecurrentBooking;
                    const cantAbsentOrCancel = hoursToStart < 1 && isRecurrentBooking;
                    const hasPendingAbsence = pendingAbsenceByBooking.has(b.booking_id);
                    const hasAcceptedAbsence = acceptedAbsenceByBooking.has(b.booking_id);
                    const hasRejectedAbsence = rejectedAbsenceByBooking.has(b.booking_id);

                    return (
                      <div key={`bk-${b.booking_id}`} style={cardStyle}>
                        <div style={{ display: "flex", gap: 12 }}>
                          {hideCoachInfo ? (
                            <div style={avatarFallback}></div>
                          ) : photo ? (
                            <img src={photo} alt={`Foto de ${b.coach_name}`} style={avatarImg} />
                          ) : (
                            <div style={avatarFallback}>{initialsFromName(b.coach_name)}</div>
                          )}

                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                              <span style={{ fontWeight: 700 }}>{fmtDateLong(apiYMD(b.start_date))}</span>
                              {b.start_time && (
                                <span style={{ fontSize: 12, color: "#374151" }}>· {b.start_time.slice(0, 5)}h</span>
                              )}
                            </div>
                            <div style={{ fontSize: 14 }}>
                              <div>🔔 Servicio: <strong>{b.service_name || "—"}</strong></div>
                              <div>🧑‍🏫 Profesional: <strong>{coachLabel}</strong></div>
                              {hideCoachInfo && (
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                  Se mostrará el entrenador cuando falte 1 hora para la sesión.
                                </div>
                              )}
                            </div>

                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!canReschedule) {
                                    alert("No puedes cambiar esta reserva: faltan menos de 12 horas para el inicio.");
                                    return;
                                  }
                                  openReschedule(b);
                                }}
                                style={{
                                  ...outlineBtnStyle,
                                  ...(canReschedule ? {} : { opacity: .5, cursor: "not-allowed" })
                                }}
                                aria-label="Cambiar hora de esta reserva"
                                disabled={cancelBusyId === b.booking_id || !canReschedule}
                                title={canReschedule ? "Cambiar hora" : `No se puede cambiar con menos de ${minHorasCambio} horas de antelación.`}
                              >
                                Cambiar hora
                              </button>

                              {hasAcceptedAbsence ? (
                                <button
                                  type="button"
                                  onClick={() => alert(acceptedAbsenceInfo)}
                                  style={{ ...outlineBtnStyle, borderColor: "#10b981", color: "#065f46" }}
                                  aria-label="Ausencia aceptada para esta reserva"
                                >
                                  Ausencia Aceptada
                                </button>
                              ) : hasRejectedAbsence ? (
                                <button
                                  type="button"
                                  onClick={() => alert(rejectedAbsenceInfo)}
                                  style={{ ...outlineBtnStyle, borderColor: "#6b7280", color: "#4b5563" }}
                                  aria-label="Ausencia rechazada para esta reserva"
                                >
                                  Ausencia Rechazada
                                </button>
                              ) : requiresAbsenceRequest ? (
                                <button
                                  type="button"
                                  onClick={() => requestAbsenceForBooking(b.booking_id)}
                                  style={{ ...outlineBtnStyle, borderColor: "#f59e0b", color: "#92400e" }}
                                  aria-label="Solicitar ausencia para esta reserva"
                                  disabled={absenceBusyId === b.booking_id || hasPendingAbsence}
                                >
                                  {hasPendingAbsence
                                    ? "Ausencia pendiente"
                                    : absenceBusyId === b.booking_id
                                      ? "Solicitando..."
                                      : "Solicitar ausencia"}
                                </button>
                              ) :  cantAbsentOrCancel ? (
                                <button
                                type="button"
                                  onClick={() => alert("No se puede solicitar ausencia con menos de 1 hora de antelacion")}
                                  style={{ ...outlineBtnStyle, borderColor: "#ef4444", color: "#b91c1c" }}
                                  aria-label="Solicitar ausencia para esta reserva"
                                >
                                  {"Solicitar ausencia"}
                                </button>
                              ) 
                              
                              :
                              (
                                <button
                                  type="button"
                                  onClick={() => cancelReservation(b.booking_id)}
                                  style={{ ...outlineBtnStyle, borderColor: "#ef4444", color: "#b91c1c" }}
                                  aria-label="Cancelar esta reserva"
                                  disabled={cancelBusyId === b.booking_id}
                                >
                                  {cancelBusyId === b.booking_id ? "Cancelando…" : "Cancelar"}
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={() => {
                                  const dateStr = apiYMD(b.start_date);
                                  const timeStr = (b.start_time || "00:00").slice(0, 5);
                                  const start = new Date(`${dateStr}T${timeStr}:00`);
                                  alert("Si modifica su sesión tras descargar el evento no se actualizara su calendario.");
                                  shareICS(b.service_name || "Evento", start);
                                }}
                                style={{ ...outlineBtnStyle, borderColor: "#57bb6dff", color: "#287e4cff" }}
                              >
                                Descargar evento
                              </button>

                              {!canReschedule && (
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  No se puede cambiar con menos de {minHorasCambio} horas de antelación.
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
              ) : sessionsTab === "recovery" ? (
                <div>
                  <div style={recoveryCountersGridStyle}>
                    <div style={recoveryCounterCardStyle}>
                      <span style={recoveryCounterLabelStyle}>Sesiones perdidas</span>
                      <strong style={recoveryCounterValueStyle}>{lostSessionsCount}</strong>
                    </div>
                    <div style={recoveryCounterCardStyle}>
                      <span style={recoveryCounterLabelStyle}>Ausencias solicitadas del mes</span>
                      <strong style={recoveryCounterValueStyle}>{`${monthlyAbsenceRequestsCount}/${totalAbsenceRequestQuota}`}</strong>
                    </div>
                    <div style={recoveryCounterCardStyle}>
                      <span style={recoveryCounterLabelStyle}>Número de recuperaciones disponibles</span>
                      <strong style={recoveryCounterValueStyle}>{`${availableRecoveriesCount}/${totalAbsenceRequestQuota}`}</strong>
                    </div>
                  </div>

                  {nonExpiredCoupons.length === 0 ? (
                    <p style={{ color: "#6b7280" }}>No tienes sesiones de recuperación vigentes.</p>
                  ) : (
                    nonExpiredCoupons.map(coupon => {
                      const cardStateStyle = coupon.used ? couponCardUsedStyle : couponCardUnusedStyle;

                      return (
                        <div key={`coupon-${coupon.id}`} style={{ ...couponCardStyle, ...cardStateStyle }}>
                          <img src={coupon.productImage} alt={coupon.productName} style={couponProductImageStyle} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <strong>{coupon.productName}</strong>
                            </div>
                            <div style={{ marginTop: 8, fontSize: 13, color: "#374151", display: "grid", gap: 4 }}>
                              <span>Fecha de solicitud: {fmtDateLong(coupon.requestedAt)}</span>
                              {coupon.approvedAt && <span>Fecha de aprobación: {fmtDateLong(coupon.approvedAt)}</span>}
                              <span>Fecha de caducidad: {coupon.expiryDate ? fmtDateLong(coupon.expiryDate) : "—"}</span>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              <span style={{ ...couponUsedFlagStyle, ...(coupon.used ? couponUsedFlagTrueStyle : couponUsedFlagFalseStyle) }}>
                                Usado: {coupon.used ? "Sí" : "No"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : pastBookings.length === 0 ? (
                <p style={{ color: "#6b7280" }}>No hay sesiones pasadas disponibles.</p>
              ) : (
                pastBookings.map(b => {
                  const photo = coachPhotoByBooking[b.booking_id] ?? null;
                  const paymentLabel = paymentStatusLabel(b.payment_status);

                  return (
                    <div key={`bk-past-${b.booking_id}`} style={{ ...cardStyle, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                      <div style={{ display: "flex", gap: 12 }}>
                        {photo ? (
                          <img src={photo} alt={`Foto de ${b.coach_name}`} style={avatarImg} />
                        ) : (
                          <div style={avatarFallback}>{initialsFromName(b.coach_name)}</div>
                        )}

                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <span style={{ fontWeight: 700 }}>{fmtDateLong(apiYMD(b.start_date))}</span>
                            {b.start_time && (
                              <span style={{ fontSize: 12, color: "#374151" }}>· {b.start_time.slice(0, 5)}h</span>
                            )}
                          </div>
                          <div style={{ fontSize: 14 }}>
                            <div>🔔 Servicio: <strong>{b.service_name || "—"}</strong></div>
                            <div>🧑‍🏫 Profesional: <strong>{b.coach_name || "—"}</strong></div>
                          </div>

                          {paymentLabel && (
                            <div style={{ marginTop: 6, fontSize: 12, color: "#374151" }}>
                              💳 Pago: <strong>{paymentLabel}</strong>
                            </div>
                          )}

                          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <span style={historyTagStyle}>⏱️ Tarde: <strong>{Number(b.is_customer_late) ? "Sí" : "No"}</strong></span>
                            <span style={historyTagStyle}>🚫 Ausente: <strong>{Number(b.is_customer_absent) ? "Sí" : "No"}</strong></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== Modal reservar ===== */}
      {showModal && (
        <div style={sheetOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div style={sheetCardStyle}>
            <ModalHeader onBack={resetAndCloseModal} />
            <div style={sheetContentStyle}>
              {/* Producto + Servicio */}
              <section style={{ marginBottom: 16 }}>
                <h4 style={sectionTitle}>Producto</h4>
                <select
                  value={selectedProductId}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value ? Number(e.target.value) : "");
                    setSelectedServiceId("");
                    setDayHours([]);
                    setSwapCandidateId("");
                    setSwapMode(false);
                    setActiveHour("");
                  }}
                  style={pillSelect}
                  disabled={!!rescheduleFrom || !!recoverMode}
                  title={rescheduleFrom ? "No modificable al reprogramar" : ""}
                >
                  <option key="prod-ph" value="">
                    {userProducts.length > 0 ? "Seleccione un producto…" : "No tienes productos contratados"}
                  </option>
                  {userProducts.map((p, idx) => {
                    const value = p.active_product_id ?? p.id;
                    return (
                      <option key={`p-${value}-${idx}`} value={value}>
                        {p.name}
                      </option>
                    );
                  })}
                </select>

                <div style={{ height: 10 }} />

                <h4 style={sectionTitle}>Servicio</h4>
                <select
                  value={selectedServiceId}
                  onChange={(e) => {
                    const v = e.target.value ? Number(e.target.value) : "";
                    setSelectedServiceId(v);
                    setDayHours([]);
                    setActiveHour("");
                    setSwapCandidateId("");
                    setSwapMode(false);
                    if (v) fetchWeeklyForDay(v, ymd(selectedDate));
                  }}
                  style={pillSelect}
                  disabled={!!rescheduleFrom || !selectedProductId}
                  title={rescheduleFrom ? "No modificable al reprogramar" : ""}
                >
                  <option key="svc-ph" value="">{selectedProductId ? "Seleccione un servicio…" : "Elige primero un producto"}</option>
                  {servicesForProduct.map(s => (<option key={`svc-${s.service_id}`} value={s.service_id}>{s.service_name}</option>))}
                </select>

                {/* Indicador de crédito usando /user-weekly-limit */}
                {!rescheduleFrom && selectedServiceId && serviceCredit && !recoverMode && (
                  <div style={{ marginTop: 8 }}>
                    {serviceCredit.mode === "weekly" ? (
                      serviceCredit.remaining > 0 ? (
                        <span style={creditPillOk}>
                          Quedan {serviceCredit.remaining} / {serviceCredit.limit} esta semana
                        </span>
                      ) : canReserveWithWildCard ? (
                        <span style={creditPillOk}>
                          Tienes una recuperacion de sesion.
                        </span>
                      ) : (
                        <span style={creditPillAlert}>
                          Has alcanzado el límite semanal para este producto
                        </span>
                      )
                    ) : serviceCredit.remaining > 0 ? (
                      <span style={creditPillOk}>
                        Quedan {serviceCredit.remaining} / {serviceCredit.limit} sesiones disponibles
                      </span>
                    ) : (
                      <span style={creditPillAlert}>
                        Ya no te quedan sesiones disponibles de este producto
                      </span>
                    )}
                  </div>
                )}
              </section>

              {/* Horario */}
              <section>
                <h4 style={sectionTitle}>Horario</h4>
                <div style={{ color: "#9ca3af", fontWeight: 600, marginBottom: 8 }}>Seleccione fecha y hora</div>
                <div style={dateRow}>
                  <input
                    type="date"
                    value={ymd(selectedDate)}
                    min={minSelectableDateYmd}
                    max={ymd(diaMax)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) return;
                      if (pendingPaymentRestriction && value <= pendingPaymentRestriction.purchaseYmd) {
                        e.currentTarget.value = ymd(selectedDate);
                        alert(`Este producto tiene el pago pendiente. Podras reservar a partir del ${fmtDateLong(pendingPaymentRestriction.minSelectableYmd)}.`);
                        return;
                      }
                      const d = parseYMD(value);
                      if (!isSelectableDate(d)) {
                        e.currentTarget.value = ymd(selectedDate);
                        alert("Fecha no disponible (pasada, festivo, domingo o fuera del rango permitido).");
                        return;
                      }
                      setSelectedDate(d);
                      setDayHours([]);
                      setActiveHour("");
                      if (selectedServiceId) fetchWeeklyForDay(Number(selectedServiceId), ymd(d));
                    }}
                    style={dateInput}
                  />
                </div>
                {pendingPaymentRestriction && (
                  <div style={{ marginTop: 6, fontSize: 13, color: "#b91c1c", fontWeight: 600 }}>
                    Este producto tiene el pago pendiente. Podras reservar a partir del {fmtDateLong(pendingPaymentRestriction.minSelectableYmd)}.
                  </div>
                )}

                <div style={tabBar}>
                  <button type="button" onClick={() => setTimeTab("am")} style={{ ...tabBtn, ...(timeTab === "am" ? tabBtnActive : {}) }}>Mañana</button>
                  <button type="button" onClick={() => setTimeTab("pm")} style={{ ...tabBtn, ...(timeTab === "pm" ? tabBtnActive : {}) }}>Tarde</button>
                </div>

                <div style={{ minHeight: 90 }}>
                  {slotsLoading && <p>Cargando horas…</p>}
                  {slotsError && <p style={{ color: "red" }}>{slotsError}</p>}
                  {!slotsLoading && !slotsError && (
                    <>
                      {dayHours.length === 0 && <p style={{ color: "#6b7280" }}>No hay profesionales disponibles en esta fecha.</p>}
                      {dayHours.length > 0 && (
                        <div style={hoursGrid}>
                          {dayHours
                            .filter(s => {
                                  const [h, m] = s.hour.split(":").map(Number);
                                  if (timeTab === "am") {
                                      // Mañana: hasta las 13:30 inclusive
                                      return h < 13 || (h === 13 && m <= 30);
                                  } else {
                                      // Tarde: desde las 14:00 en adelante
                                      return h > 13 || (h === 13 && m > 30);
                                  }
                              })
                            .map(slot => {
                              function toMinutes(hhmm: string) {
                                const [h, m] = hhmm.split(":").map(Number);
                                return h * 60 + m;
                              }
                              const isToday = slot.date === todayStr;
                              const hhmm = slot.hour.slice(0, 5);
                              const now = new Date();
                              const nowMins = now.getHours() * 60 + now.getMinutes();
                              const slotMins = toMinutes(hhmm);
                              const isPastHour = isToday && slotMins <= nowMins;

                              // Mantiene tu lógica de 12h, solo para tooltip
                              const slotDateTime = new Date(`${slot.date}T${hhmm}:00`);
                              const diffHours = (slotDateTime.getTime() - Date.now()) / 36e5;
                              const tooSoon = diffHours < 1;

                              const isFull = Number(slot.booked ?? 0) >= Number(slot.capacity ?? 1);

                              return (
                                <button
                                  type="button"
                                  key={`slot-${slot.date}-${slot.hour}-${slot.service_id}`}
                                  onClick={() => {
                                    if (!isPastHour) {
                                      setActiveHour(hhmm);
                                      loadHourDetail(slot.hour);
                                    }
                                  }}
                                  disabled={isPastHour || isFull || tooSoon}
                                  style={{
                                    ...timeBtn,
                                    ...(activeHour === hhmm
                                      ? { border: "2px solid #d11f2a", boxShadow: "0 0 0 2px rgba(209,31,42,.15)" }
                                      : { border: "1px solid #e5e7eb", boxShadow: "none" }),
                                    ...(isPastHour ? { opacity: 0.5, cursor: "not-allowed" } : {})
                                  }}
                                  title={

                                    isFull
                                      ? "Hora completa"
                                      : tooSoon
                                      ? `No se puede reservar con menos de 1 hora de antelación`
                                      : isPastHour
                                      ? "Hora pasada, no disponible"
                                      : ""
                                  }
                                >
                                  {hhmm}
                                </button>
                              );
                            })}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Selección de profesional SOLO fisioterapia o VIP manual */}
                {(() => {
                  const requiresManual =
                    !!selectedServiceId &&
                    (isPhysioService(Number(selectedServiceId)) ||
                      isVipManualEligible(Number(selectedServiceId), services, isVip));

                  if (!requiresManual || !hourDetail.hour || hourDetail.loading || hourDetail.error) return null;

                  const label =
                    isPhysioService(Number(selectedServiceId))
                      ? "Selecciona profesional (fisioterapia)"
                      : "Selecciona profesional (VIP)";

                  return (
                    <div style={{ marginTop: 14 }}>
                      <label style={{ display: "block", fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
                        {label}
                      </label>
                      <select
                        value={hourDetail.manualCoachId ?? ""}
                        onChange={(e) =>
                          setHourDetail(h => ({ ...h, manualCoachId: e.target.value ? Number(e.target.value) : null }))
                        }
                        style={pillSelect}
                      >
                        <option value="">Elige un profesional...</option>
                        {hourDetail.coaches.map(c => (
                          <option key={`coach-${c.coach_id}`} value={c.coach_id}>{c.coach_name}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* ==== BLOQUE DE CAMBIO DE RESERVA ==== */}
                {shouldForceSwapFlow && (
                  <div style={{ marginTop: 14, padding: 12, border: "1px solid #fecaca", background: "#fef2f2", borderRadius: 10 }}>
                    <div style={{ fontWeight: 800, marginBottom: 6 }}>
                      {serviceCredit.mode === "weekly"
                        ? "Has alcanzado el límite semanal"
                        : "No te quedan sesiones disponibles en este bono"}
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      {serviceCredit.mode === "weekly"
                        ? <>Elige una de tus reservas de <strong>esta semana</strong> para cancelarla y usarla aquí:</>
                        : serviceCredit.mode === "total"
                          ? <>Elige una reserva futura de <strong>cualquiera de los servicios del pool</strong> para cancelarla y usarla aquí:</>
                          : <>Elige una reserva futura del <strong>mismo bono</strong> para cancelarla y usarla aquí:</>}
                    </div>

                    {swapCandidates.length === 0 ? (
                      <div style={{ color: "#6b7280" }}>
                        {serviceCredit.mode === "weekly"
                          ? "No tienes reservas del mismo bono en esta semana."
                          : "No tienes reservas futuras intercambiables en este bono."}
                      </div>
                    ) : (
                      <>
                        <select
                          value={swapCandidateId}
                          onChange={(e) => {
                            const id = e.target.value ? Number(e.target.value) : "";
                            setSwapCandidateId(id);
                            if (id) {
                              const found = swapCandidates.find(c => c.id === id);
                              setSwapCandidateDate(found ? new Date(found.start_date) : null);
                            } else {
                              setSwapCandidateDate(null);
                            }
                          }}
                          style={pillSelect}
                        >
                          <option value="">Selecciona una reserva…</option>
                          {swapCandidates.map(c => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </select>

                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <button
                            type="button"
                            onClick={() => { setSwapMode(true); swapAndCreate(); }}
                            disabled={
                              swapBusy ||
                              !hourDetail.hour ||
                              !swapCandidateId ||
                              (() => {
                                const requiresManual =
                                  !!selectedServiceId &&
                                  (isPhysioService(Number(selectedServiceId)) ||
                                    isVipManualEligible(Number(selectedServiceId), services, isVip));
                                return requiresManual && !hourDetail.manualCoachId;
                              })()
                            }
                            style={{
                              ...reserveCta,
                              ...(swapBusy ||
                                !hourDetail.hour ||
                                !swapCandidateId ||
                                ([3, 19].includes(Number(selectedServiceId)) && !hourDetail.manualCoachId)
                                ? reserveCtaDisabled : {}),
                            }}
                          >
                            {swapBusy ? "Cambiando…" : "Cancelar seleccionada y reservar esta"}
                          </button>
                        </div>

                        {!hourDetail.hour && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                            Selecciona una <strong>hora</strong> para activar el cambio.
                          </div>
                        )}
                      </>
                    )}

                    {swapError && <div style={{ color: "#b91c1c", marginTop: 8 }}>{swapError}</div>}
                  </div>
                )}
              </section>
            </div>
            {!shouldForceSwapFlow && (
              <div style={sheetBottomBar}>
                <button
                  type="button"
                  onClick={createBooking}
                  disabled={
                    bookingBusy ||
                    !selectedProductId ||
                    !selectedServiceId ||
                    !hourDetail.hour ||
                    ([3, 19].includes(Number(selectedServiceId)) && !hourDetail.manualCoachId)
                  }
                  style={{
                    ...reserveCta,
                    ...(bookingBusy ||
                      !selectedProductId ||
                      !selectedServiceId ||
                      !hourDetail.hour ||
                      ([3, 19].includes(Number(selectedServiceId)) && !hourDetail.manualCoachId)
                      ? reserveCtaDisabled : {}),
                  }}
                >
                  {rescheduleFrom ? "Cambiar hora" : recoverMode ? "Recuperar" : "Reservar"}
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ===== Modal historial (DETALLADO) ===== */}
      {showPastModal && (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="past-modal-title">
          <div style={modalCardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 id="past-modal-title" style={{ margin: 0, fontSize: 18 }}>Sesiones — {fmtDateLong(ymd(selectedDate))}</h3>
              <button type="button" onClick={() => setShowPastModal(false)} style={closeBtnStyle} aria-label="Cerrar">✕</button>
            </div>

            {pastLoading && <p>Cargando…</p>}
            {pastError && <p style={{ color: "red" }}>{pastError}</p>}
            {!pastLoading && !pastError && (
              <>
                {pastSessions.length === 0 && <p style={{ color: "#6b7280" }}>No se encontraron sesiones.</p>}
                {pastSessions.map(s => (
                  <div key={`past-${s.booking_id}`} style={cardStyle}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ fontWeight: 700 }}>{s.time ? `${s.time}h` : "—"}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>#{s.booking_id}</div>
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                      <div>🧩 Producto: <strong>{s.product_name || "—"}</strong></div>
                      <div>🔔 Servicio: <strong>{s.service_name || "—"}</strong></div>
                      <div>🧑‍🏫 Profesional: <strong>{s.coach_name || "—"}</strong></div>
                      {(s.payment_status === "paid" || s.payment_status === "Pagado") && (
                        <div>💳 Pago: <strong>Pagado</strong></div>
                      )}
                      {(s.payment_status === "pending" || s.payment_status === "Pendiente") && (
                        <div>💳 Pago: <strong>Pendiente</strong></div>
                      )}
                      {(s.payment_status === "cancelled" || s.payment_status === "Cancelado") && (
                        <div>💳 Pago: <strong>Cancelado</strong></div>
                      )}
                      {s.comment && <div>📝 Comentario: <em>{s.comment}</em></div>}
                      <div style={{ marginTop: 6, display: "flex", gap: 10 }}>
                        <span title="Llegó tarde" style={{ padding: "2px 8px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb" }}>
                          ⏱️ Tarde: <strong>{s.is_customer_late ? "Sí" : "No"}</strong>
                        </span>
                        <span title="Ausencia" style={{ padding: "2px 8px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb" }}>
                          🚫 Ausente: <strong>{s.is_customer_absent ? "Sí" : "No"}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== Cuestionario ===== */}
      {cuestionarioActivo && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              maxWidth: 400,
              width: "90%",
            }}
          >
            <h3>Cuestionario Pre-Entreno</h3>
            <p style={{ fontWeight: "bold" }}>¿Cómo te encuentras en general?</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button type="button" onClick={() => responderPregunta("Muy bien")} style={cuestionarioBtn}>⏫ Muy bien</button>
              <button type="button" onClick={() => responderPregunta("Bien")} style={cuestionarioBtn}>⬆️ Bien</button>
              <button type="button" onClick={() => responderPregunta("Normal")} style={cuestionarioBtn}>➖ Normal</button>
              <button type="button" onClick={() => responderPregunta("Mal")} style={cuestionarioBtn}>⬇️ Mal</button>
              <button type="button" onClick={() => responderPregunta("Muy mal")} style={cuestionarioBtn}>⏬ Muy mal</button>
            </div>

            <div style={{ marginTop: 12, textAlign: "right" }}>
              <button type="button" onClick={omitirFormulario} style={{ color: "#d11f2a" }}>Omitir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== Helpers =================== */
function normalizeUserProducts(raw: any): UserProduct[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((p: any) => ({
    id: p.id ?? p.product_id,
    name: p.name ?? p.product_name,
    image: normalizeProductImageUrl(p.image ?? p.photo ?? p.product_image ?? p.image_url ?? null),
    active_product_id: p.active_product_id ?? null,
    price: p.price ?? p.sell_price ?? null,
    serviceIds: Array.isArray(p.serviceIds) ? p.serviceIds.map(Number)
      : Array.isArray(p.service_ids) ? p.service_ids.map(Number)
        : typeof p.service_ids === "string"
          ? (() => {
            const rawIds = String(p.service_ids).trim();
            if (!rawIds) return [];
            try {
              const parsed = JSON.parse(rawIds);
              if (Array.isArray(parsed)) return parsed.map(Number).filter(Number.isFinite);
            } catch {
              // ignore JSON parse errors and fallback to comma-separated format
            }
            return rawIds
              .split(",")
              .map((x: string) => Number(x.trim()))
              .filter(Number.isFinite);
          })()
          : Number.isFinite(Number(p.service_ids))
            ? [Number(p.service_ids)]
            : [],
    // Preserve expiry / subscription fields if present (added to userProducts schema)
    expiry_date: p.expiry_date ?? p.expiryDate ?? null,
    subscription_status: p.subscription_status ?? p.subscriptionStatus ?? null,
    type_of_product: p.type_of_product ?? p.typeOfProduct ?? null,
    payment_status: p.payment_status ?? p.paymentStatus ?? null,
    payment_method: p.payment_method ?? p.paymentMethod ?? null,
    purchase_date: p.purchase_date ?? p.purchaseDate ?? null,
    active_product_status: p.active_product_status ?? p.activeProductStatus ?? null,
    is_canceled_subscription:
      p.is_canceled_subscription == null && p.isCanceledSubscription == null
        ? null
        : Number(p.is_canceled_subscription ?? p.isCanceledSubscription),
    target_date: p.target_date ?? p.targetDate ?? null,
    eligible_for_target_date:
      typeof p.eligible_for_target_date === "boolean"
        ? p.eligible_for_target_date
        : typeof p.eligibleForTargetDate === "boolean"
          ? p.eligibleForTargetDate
          : undefined,
    expired_for_target_date:
      typeof p.expired_for_target_date === "boolean"
        ? p.expired_for_target_date
        : typeof p.expiredForTargetDate === "boolean"
          ? p.expiredForTargetDate
          : undefined,
    recovery_eligible_for_target_date:
      typeof p.recovery_eligible_for_target_date === "boolean"
        ? p.recovery_eligible_for_target_date
        : typeof p.recoveryEligibleForTargetDate === "boolean"
          ? p.recoveryEligibleForTargetDate
          : undefined,
    sessionsRemaining:
      (typeof p.sesions === "number" ? p.sesions :
        typeof p.sessions === "number" ? p.sessions :
          typeof p.remaining_sessions === "number" ? p.remaining_sessions :
            typeof p.sessions_remaining === "number" ? p.sessions_remaining : null),
    centro: p.centro ?? null,
  })).filter(x => x.id && x.name);
}

function normalizePreferredCoaches(raw: any): PreferredCoachItem[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .filter((item: any) => Number.isInteger(item?.coach_id))
    .map((item: any) => ({
      coach_id: Number(item.coach_id),
      priority: Number.isInteger(item.priority) ? Number(item.priority) : null,
    }))
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
    .slice(0, 3)
    .map((item, idx) => ({ ...item, priority: idx + 1 }));
}

function hoursUntil(dateIso: string, timeHHMMSS?: string) {
  const hhmmss = (timeHHMMSS || "00:00:00").slice(0, 8);
  const start = new Date(`${dateIso}T${hhmmss}`); // local TZ
  return (start.getTime() - Date.now()) / 36e5;   // ms → horas
}

function isPhysioService(id?: number | string) {
  const num = Number(id);
  return [3, 19].includes(num);
}

function isTrainingService(id: number | string, servicesList: ServiceItem[]) {
  const svc = servicesList.find(s => Number(s.service_id) === Number(id));
  const name = (svc?.service_name || "").toLowerCase();
  if (!name || isPhysioService(id)) return false;
  return name.includes("entren") && !name.includes("nutric");
}

function isTrainingServiceName(serviceName?: string | null) {
  const normalized = (serviceName || "").toLowerCase();
  if (!normalized) return false;
  if (normalized.includes("nutric")) return false;
  if (normalized.includes("fisio")) return false;
  return normalized.includes("entren");
}

function pickPreferredCoach(coaches: HourlyCoach[], favorites: PreferredCoachItem[], isTraining: boolean) {
  if (!isTraining || !Array.isArray(coaches) || coaches.length === 0) return null;
  if (!Array.isArray(favorites) || favorites.length === 0) return null;

  for (const fav of favorites) {
    const match = coaches.find(c => c.coach_id === fav.coach_id);
    if (match) return fav.coach_id;
  }
  return null;
}

function isVipManualEligible(
  id: number | string,
  servicesList: ServiceItem[],
  vipFlag: boolean
) {
  if (!vipFlag) return false;
  const svc = servicesList.find(s => Number(s.service_id) === Number(id));
  const name = (svc?.service_name || "").toLowerCase();
  return name.includes("entren") || name.includes("nutric");
}

/* =================== Estilos =================== */
const navBtnStyle: React.CSSProperties = {
  width: 36, height: 32, borderRadius: 8, background: "#fff", fontSize: 18, lineHeight: "30px", cursor: "pointer",
  border: "1px solid #e5e7eb",
};
const dayCellStyle: React.CSSProperties = {
  height: 34, padding: 0, borderRadius: 10, background: "#f3f4f6",
  border: "1px solid #e5e7eb",
  color: "#111827", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
};
const dayCellReservedStyle: React.CSSProperties = { background: "#dbeafe", border: "1px solid #93c5fd", color: "#1d4ed8" };
const dayCellHolidayStyle: React.CSSProperties = { background: "#374151", color: "#e5e7eb", border: "1px solid #374151" };
const dayCellSelectedStyle: React.CSSProperties = { background: "#1d4ed8", color: "#fff", border: "1px solid #1d4ed8" };
const dayCellTodayStyle: React.CSSProperties = { border: "3px solid #334155" };

const cardStyle: React.CSSProperties = { padding: 12, borderRadius: 10, background: "#e3f2fd", border: "1px solid #cfe8ff", marginBottom: 10 };

const sheetOverlayStyle: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "flex", justifyContent: "center", alignItems: "stretch", zIndex: 30 };
const modalOverlayStyle: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 40 };
const modalCardStyle: React.CSSProperties = { width: "100%", maxWidth: 500, background: "#fff", borderRadius: 12, padding: "24px 18px", boxShadow: "0 8px 32px rgba(0,0,0,.18)", maxHeight: "90vh", overflowY: "auto" };
const sheetCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 600,
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  maxHeight: "95vh",
  borderRadius: "12px",
  overflow: "hidden"
};

const sheetContentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "16px 16px 90px"
};
const sheetBottomBar: React.CSSProperties = { position: "sticky", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: "#fff", boxShadow: "0 -6px 20px rgba(0,0,0,.08)" };

const sectionTitle: React.CSSProperties = { margin: "0 0 8px", fontSize: 18, fontWeight: 800 };
const pillSelect: React.CSSProperties = { width: "100%", borderRadius: 12, border: "1px solid #e5e7eb", padding: "12px 14px", fontSize: 16, background: "#fff" };

const dateRow: React.CSSProperties = { position: "relative", display: "flex", alignItems: "center", marginBottom: 12 };
const dateInput: React.CSSProperties = { width: "100%", borderRadius: 12, border: "1px solid #d1d5db", padding: "12px 44px 12px 14px", fontSize: 16 };

const tabBar: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, borderBottom: "1px solid #e5e7eb", margin: "6px 0 10px" };
const tabBtn: React.CSSProperties = { background: "transparent", borderWidth: 0, padding: "10px 4px", fontWeight: 700, color: "#9ca3af", borderBottom: "2px solid transparent", cursor: "pointer" };
const tabBtnActive: React.CSSProperties = { color: "#d11f2a", borderBottom: "2px solid #d11f2a" };
const tabBtnDisabled: React.CSSProperties = { opacity: 0.45, color: "#9ca3af", cursor: "not-allowed" };
const recoveryCountersGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 10,
  marginBottom: 12,
};
const recoveryCounterCardStyle: React.CSSProperties = {
  border: "1px solid #dbe7ff",
  background: "#f8faff",
  borderRadius: 12,
  padding: "10px 12px",
  display: "grid",
  gap: 4,
};
const recoveryCounterLabelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#4b5563",
  fontWeight: 600,
};
const recoveryCounterValueStyle: React.CSSProperties = {
  fontSize: 24,
  lineHeight: 1,
  color: "#111827",
};
const couponCardStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  padding: 12,
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#fff",
  marginBottom: 10,
};
const couponCardUnusedStyle: React.CSSProperties = {
  background: "#ecfdf3",
  borderColor: "#86efac",
};
const couponCardUsedStyle: React.CSSProperties = {
  background: "#f3f4f6",
  borderColor: "#d1d5db",
};
const couponProductImageStyle: React.CSSProperties = {
  width: 76,
  height: 76,
  borderRadius: 10,
  objectFit: "cover",
  border: "1px solid #e5e7eb",
};
const couponUsedFlagStyle: React.CSSProperties = {
  borderRadius: 999,
  padding: "3px 10px",
  fontSize: 12,
  fontWeight: 700,
  border: "1px solid",
};
const couponUsedFlagTrueStyle: React.CSSProperties = {
  background: "#e5e7eb",
  color: "#374151",
  borderColor: "#d1d5db",
};
const couponUsedFlagFalseStyle: React.CSSProperties = {
  background: "#dbeafe",
  color: "#1e40af",
  borderColor: "#93c5fd",
};

const hoursGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
const timeBtn: React.CSSProperties = { padding: "14px 10px", borderRadius: 12, background: "#fff", border: "1px solid #e5e7eb", fontSize: 18, fontWeight: 700, cursor: "pointer" };

const reserveCta: React.CSSProperties = { width: "100%", padding: "16px 14px", borderRadius: 999, background: "#d11f2a", color: "#fff", borderWidth: 0, fontWeight: 800, fontSize: 20, cursor: "pointer" };
const reserveCtaDisabled: React.CSSProperties = { opacity: .5, cursor: "not-allowed" };

const closeBtnStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 8, background: "rgba(0,0,0,0.07)", color: "#374151",
  border: "none", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center"
};
const historyTagStyle: React.CSSProperties = {
  padding: "2px 8px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  color: "#374151",
};

const COACH_PHOTO_SIZE = 84;
const avatarImg: React.CSSProperties = {
  width: COACH_PHOTO_SIZE, height: COACH_PHOTO_SIZE, borderRadius: 12, objectFit: "cover", display: "block",
  border: "1px solid #cfe8ff", background: "#fff",
};
const avatarFallback: React.CSSProperties = {
  width: COACH_PHOTO_SIZE, height: COACH_PHOTO_SIZE, borderRadius: 12, background: "#bfdbfe", color: "#1e3a8a",
  display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22,
};
const outlineBtnStyle: React.CSSProperties = {
  padding: "8px 12px", borderRadius: 999, background: "#fff", border: "1px solid #93c5fd",
  color: "#1d4ed8", fontWeight: 700, fontSize: 14, cursor: "pointer",
};

const creditPillBase: React.CSSProperties = {
  display: "inline-block",
  padding: "6px 10px",
  borderRadius: 999,
  fontWeight: 700,
  fontSize: 12,
  border: "1px solid",
};
const creditPillOk: React.CSSProperties = {
  ...creditPillBase, color: "#065f46", background: "#d1fae5", borderColor: "#a7f3d0",
};
const creditPillAlert: React.CSSProperties = {
  ...creditPillBase, color: "#991b1b", background: "#fee2e2", borderColor: "#fecaca",
};
const cuestionarioBtn: React.CSSProperties = {
  padding: "10px",
  borderRadius: 8,
  background: "#f3f4f6",
  cursor: "pointer",
  border: "1px solid #ddd",
  fontSize: 16,
  textAlign: "left",
};
